import React from "react";
import { EmptyState, Icon, notify, confirmDialog, ListRow } from "./registry";
import { templateFor, DEFAULT_TEMPLATES, JOB_STATUS_TEMPLATES, LEGACY_UPSELL_RULES, CROSS_SELL_RULES } from "./lib/servicewords";   // the words per service (plan §5.6) — the server resolves the same table
import { WEATHER_DEFAULTS } from "./lib/weatherrules";   // the concrete weather thresholds the sliders start from
import { enabledVerticals, brandOf } from "./lib/verticals";   // does this workspace run concrete; the concrete brand the agent answers as
import { concreteKnowledge } from "./lib/concrete.knowledge";   // the concrete half of what the receptionist knows
/* ============================================================
   Fence Works — AI suite: Receptionist + Automations
   "Never miss a call" + an automation library that runs on
   the events the app already emits.
   ============================================================ */

/* ---- automation catalog ----
   Each card maps 1:1 to a definition in automations.js by `def` (the
   canonical id). `channelType` mirrors that definition and drives which
   controls the Manage screen shows (see ManageControls):
     "message"  → SMS/Email/Both send channel + template + quiet hours
     "voice"    → voice/chat controls (no send channel)
     "rules"    → when→then rule rows
     "schedule" → sliders (days-before / rain % / radius)
     "internal" → tag lists / supplier / source toggles
   `trig` is the event router.js listens for. Config keys written here are
   exactly the keys each run(ctx) reads in automations.js.                */
type AutoCard = {
  id: string; def: string; channelType: string; trig: string; group: string;
  name: string; icon: string; blurb: string; default: boolean;
  star?: boolean;      // shown with a ★ as a recommended pick
  soon?: boolean;      // no event source wired yet — toggle disabled
  needs?: string;      // key into /automations/readiness for its prerequisite
  newCfg?: any;        // config a NEW workspace starts with (lib/supabase.ts persistAI); existing ones don't get it
  lensHidden?: string[];   // services the card doesn't apply to (811 under the Concrete lens)
};
const AI_AUTOMATIONS: AutoCard[] = [
  { id: "missed_call_text", def: "missed_call", channelType: "message", trig: "call.missed", group: "Lead capture", name: "Missed-call text-back", icon: "phone",
    blurb: "If the AI receptionist can't close a booking, instantly text the caller a link to the instant-estimate widget.", default: true },
  { id: "web_chat", def: "web_chat", channelType: "voice", trig: "widget.chat.message", group: "Lead capture", name: "Web chat concierge", icon: "mail", star: true,
    blurb: "The text sibling of the voice agent — answers website visitors 24/7, captures the lead, and drops the widget link.", default: true },
  { id: "bilingual", def: "bilingual", channelType: "voice", trig: "call.inbound.language_detected", group: "Lead capture", name: "Bilingual / Spanish receptionist", icon: "user", star: true,
    blurb: "A voice toggle that handles Spanish-speaking callers — real demand in the trades, near-free to add, a clear differentiator.", default: false },
  { id: "speed_to_lead", def: "speed_lead", channelType: "message", trig: "lead.created", group: "Lead capture", name: "Speed-to-lead reply", icon: "bolt",
    blurb: "New website/widget lead gets an SMS + email within 60 seconds while they're still hot.", default: true },
  { id: "lead_parser", def: "lead_parser", channelType: "internal", trig: "inbound.email", group: "Lead capture", name: "Inbound lead parser", icon: "search", needs: "lead_parser",
    blurb: "Angi / Nextdoor / contact-form leads auto-parsed into the CRM and speed-to-lead'd.", default: false },

  { id: "attach_upsell", def: "upsell", channelType: "rules", trig: "job.completed", group: "Sales", name: "Cross-sell / upsell", icon: "bolt", star: true, needs: "attach_upsell",
    blurb: "The crew ticks what they noticed on site \u2014 sunken or cracked concrete, an old or leaning fence \u2014 and when the job is done the customer gets one offer from the service that does that work (a fence crew\u2019s note goes out as the concrete brand, with its estimate form). Held for your approval by default.", default: true },
  { id: "estimate_confirm", def: "estimate_confirm", channelType: "message", trig: "estimate.scheduled", group: "Sales", name: "Estimate confirm + reminder", icon: "calendar", star: true,
    blurb: "Auto-confirm the on-site estimate and remind the day before — plugs silent no-show revenue leaks.", default: true },
  { id: "quote_followup", def: "quote_followup", channelType: "message", trig: "quote.sent.unsigned", group: "Sales", name: "Quote follow-up sequence", icon: "mail",
    blurb: "Unsigned quote? Auto-nudge on day 2, 6, 12 and 21, plus a heads-up before it expires \u2014 and stop the moment they sign, pay or reply. Most quotes die of silence, not price.", default: true,
    // on for new accounts, off for existing ones (the server reads a missing
    // setting as off; lib/supabase.ts writes this on a new workspace's first save)
    newCfg: { expiryReminder: { on: true, days: 3 } } },
  { id: "financing_prequal", def: "financing", channelType: "message", trig: "quote.created", group: "Sales", name: "Financing pre-qual in-flow", icon: "dollar", needs: "financing_prequal",
    blurb: "For $20k decks / big fences, offer financing mid-conversation to lift close rates.", default: false },
  { id: "abandoned_quote", def: "abandoned_quote", channelType: "message", trig: "widget.quote.abandoned", group: "Sales", name: "Abandoned-quote recovery", icon: "undo",
    blurb: "Someone starts the widget estimator, sees their number, and leaves without submitting \u2014 nudge them with the estimate they already saw. Different from Quote follow-up: that one chases quotes YOU sent; this one catches the ones that never became a quote at all.", default: true },

  { id: "appt_reminder", def: "appt_reminder", channelType: "message", trig: "appointment.upcoming", group: "Scheduling", name: "Install reminder", icon: "calendar", star: true,
    blurb: "The day before an install, remind the customer so the crew rolls up to an expecting homeowner \u2014 gate unlocked, dogs in, cars moved. Fewer wasted trips than anything else here.", default: true },
  { id: "appt_confirm", def: "appt_confirm", channelType: "message", trig: "appointment.upcoming", group: "Scheduling", name: "Appointment confirmation request", icon: "check",
    blurb: "Sent when the job books: reply C to confirm. A confirmation stamps the job so you can see who\u2019s locked in; a \u201creschedule\u201d reply flags it for you instead of becoming a no-show.", default: true },
  // FW-017 / L12: this was the "negative-review interceptor" and it routed by
  // score — 4–5★ to Google, 1–3★ to a private form with no public-review
  // option. Google's prohibited-content policy forbids selectively soliciting
  // positive reviews, so the gate is gone. What's left is worth having: you
  // hear about a problem before it's public, and the customer chooses.
  { id: "review_interceptor", def: "neg_review", channelType: "message", trig: "job.completed", group: "Reputation", name: "Feedback-first review request", icon: "bolt", star: true, needs: "review_interceptor",
    blurb: "A quick 'how'd we do?' on your own feedback page — a low rating pings you straight away, and every customer is then offered the same choice: leave a public review, or send you a private note.", default: true },
  { id: "review_request", def: "auto_review", channelType: "message", trig: "job.completed", group: "Reputation", name: "Auto review request", icon: "check", needs: "review_request",
    blurb: "On job completion, text the customer your Google review link — timed for the moment they're happiest.", default: true },
  { id: "review_replies", def: "review_responses", channelType: "internal", trig: "google.review.created", group: "Reputation", name: "Auto-draft review responses", icon: "mail", needs: "review_replies",
    blurb: "Agent drafts replies to incoming Google reviews for one-tap approval.", default: false },
  { id: "photo_capture", def: "photo_capture", channelType: "message", trig: "job.completed", group: "Reputation", name: "Finished-job photo capture", icon: "box",
    blurb: "Fires the moment a job is marked Complete. Best practice is the crew shooting the fence before they leave (Work order \u2192 Photos) \u2014 this is the backstop for when they didn\u2019t, and the source for social posts and your portfolio.", default: false },
  { id: "referral_nudge", def: "referral_nudge", channelType: "message", trig: "google.review.created", group: "Reputation", name: "Referral ask after a 5★", icon: "user",
    blurb: "A 5★ review lands → the customer gets one text with their referral link (\"know anyone who needs a fence? $100 on us\"). The moment they're happiest is the moment they'll refer.", default: false },
  { id: "review_share", def: "review_share", channelType: "internal", trig: "google.review.created", group: "Reputation", name: "Share positive reviews", icon: "send", needs: "review_share",
    blurb: "A 4–5★ Google review comes in → it becomes a thank-you post on your Facebook Page & Google profile. Social proof on autopilot.", default: false },

  { id: "diggers_811", def: "diggers", channelType: "schedule", trig: "job.booked", group: "Ops", name: "811 / Diggers Hotline reminder", icon: "mappin", star: true, lensHidden: ["concrete"],
    blurb: "Fence-specific and legally required before every dig — auto-schedule the locate when a job books.", default: true },
  { id: "job_status", def: "job_status", channelType: "jobevents", trig: "crew.enroute", group: "Ops", name: "Customer job updates", icon: "phone", star: true,
    blurb: "Every heads-up the customer wants, in one place: crew on the way, work started, job finished, install pushed. Each one switches on or off separately \u2014 this replaces the old separate \u201cOn my way\u201d automation so nobody gets texted twice for the same event.", default: false },
  { id: "reschedule_weather", def: "weather", channelType: "schedule", trig: "cron.daily.forecast", group: "Ops", name: "Weather reschedule alerts", icon: "calendar",
    blurb: "Checks the forecast against every scheduled job each morning. A fence day is graded on rain; a concrete day on what the job is \u2014 a dry slab, 40\u00b0F and the dew point for a coating or sealer, a downpour or frozen ground for a lift. Over the line, it flags the job on your schedule and drafts the reschedule note \u2014 it does NOT move the job or text the customer on its own; you approve the send.", default: false },
  { id: "permit_reminder", def: "permit", channelType: "internal", trig: "job.booked", group: "Ops", name: "Permit reminder", icon: "clipboard",
    blurb: "Flag jobs that need a county/municipal permit before the crew rolls.", default: false },
  { id: "material_reorder", def: "low_stock", channelType: "internal", trig: "job.paid", group: "Ops", name: "Low-stock reorder nudge", icon: "box",
    blurb: "When a job is paid, compares what it consumed against your Inventory on-hand and drafts a purchase order for anything short. Needs per-product BOMs and live on-hand counts in Inventory \u2014 without those it has nothing to compare.", default: false },
  { id: "smart_dispatch", def: "dispatch", channelType: "internal", trig: "workorder.created", group: "Ops", name: "Smart crew dispatch", icon: "user", needs: "smart_dispatch",
    blurb: "Auto-assigns a new work order to the nearest crew with capacity. You already assign crews by hand on the Schedule \u2014 this only earns its place once you run multiple crews and track their locations and weekly capacity.", default: false },

  { id: "ar_followup", def: "invoice", channelType: "message", trig: "invoice.overdue", group: "Money", name: "Invoice + payment follow-up", icon: "dollar", star: true,
    blurb: "Overdue-balance nudges. AR is where contractors quietly bleed — automating it is real recovered cash.", default: true },
  { id: "owner_summary", def: "pipeline", channelType: "message", trig: "cron.daily.summary", group: "Money", name: "Daily pipeline summary", icon: "activity",
    blurb: "Morning text to the owner: leads in, quotes out, jobs booked, cash collected.", default: false },

  { id: "neighborhood_farm", def: "farming", channelType: "message", trig: "job.completed", group: "Growth", name: "Neighborhood farming", icon: "mappin", star: true, needs: "neighborhood_farm",
    blurb: "\u201cWe just built a fence on your street\u201d to the surrounding addresses \u2014 one of the highest-ROI plays in home services, but only by MAIL. Neighbors never gave you their number, so texting them violates your A2P campaign and federal TCPA regardless of state. Address data is coming from parcel records; phone/email are not.", default: false },
  { id: "social_post", def: "social_post", channelType: "internal", trig: "job.completed", group: "Growth", name: "Auto-post finished jobs", icon: "image", star: true, needs: "social_post",
    blurb: "Job marked complete → its before & after photos + an AI caption go up on your Facebook Page and Google Business Profile. Approve each post, or let it run.", default: false },
  { id: "warranty_checkin", def: "warranty", channelType: "message", trig: "cron.job.anniversary", group: "Growth", name: "Warranty / re-seal check-in", icon: "undo",
    blurb: "Automated check-in on past jobs at whatever interval you set → turns into repair / re-seal / new-section leads. Recurring revenue from your own base. Concrete has its own interval and words (a yearly re-seal), set under the Concrete lens.", default: false },
];
/* the cards that apply under a lens (811 is a fence thing) */
const cardsFor = (lens: any) => AI_AUTOMATIONS.filter((a) => !(a.lensHidden || []).includes(String(lens || "fence")));

/* channel-type chip shown on each automation card */
const CHAN = {
  message:  { label: "SMS · Email", icon: "mail" },
  jobevents:{ label: "SMS · Email", icon: "mail" },
  voice:    { label: "Voice", icon: "phone" },
  rules:    { label: "Rules", icon: "sliders" },
  schedule: { label: "Scheduled", icon: "calendar" },
  internal: { label: "Background", icon: "bolt" },
};
function chanFor(a) {
  if (a.def === "web_chat") return { label: "Web chat", icon: "mail" };
  if (a.def === "social_post" || a.def === "review_share") return { label: "Social", icon: "send" };
  return CHAN[a.channelType] || CHAN.message;
}
function ChanChip({ a }: any) {
  const ch = chanFor(a);
  return <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--fg-3)", border: "1px solid var(--line-2)", borderRadius: 6, padding: "3px 7px", whiteSpace: "nowrap", flexShrink: 0 }}><Icon name={ch.icon} size={11} />{ch.label}</span>;
}

/* Compose the receptionist's BUSINESS KNOWLEDGE from live workspace data —
   customer-facing $/ft per product (priced through the same engine as quotes),
   gate pricing, deposit policy, financing, service area, process FAQ. Synced
   into the agent's prompt at activation and on demand, so it answers pricing
   and "what do you offer" questions with this contractor's real numbers. */
function buildAgentKnowledge(store: any) {
  try {
    const s = store.settings || {};
    const VFP: any = (window as any).VFP;
    const cat = [...(store.customProducts || []), ...((VFP && VFP.PRODUCTS) || [])].filter((p: any) => p && p.name);
    const lines: string[] = [];
    for (const p of cat.slice(0, 8)) {
      try {
        const q0 = VFP.computeQuote({ linearFeet: 150, gates: 0, corners: 0 }, p, s);
        const q1 = VFP.computeQuote({ linearFeet: 150, gates: 1, corners: 0 }, p, s);
        const perFt = Math.round(q0.customerTotal / 150);
        const gate = Math.max(0, Math.round(q1.customerTotal - q0.customerTotal));
        if (perFt > 0) lines.push(`- ${p.name}: ~$${perFt}/ft installed${gate ? `; walk gate ~$${gate} each` : ""}`);
      } catch (e) { /* product without a usable cost sheet — skip */ }
    }
    const dep = s.deposit || {};
    const depTxt = dep.mode === "full_only" ? "Full payment is collected up front (no deposit option)."
      : dep.mode === "custom" ? `A ${Number(dep.customPct) || 50}% deposit books the job; balance is due on completion.`
      : `Customer chooses a ${(dep.options && dep.options.length ? dep.options : [25, 50]).join("% or ")}% deposit (or pays in full); balance due on completion.`;
    const fin = s.financing || {};
    const finLive = !!(fin.enabled && s.integrations && s.integrations.wisetack && s.integrations.wisetack.connected);
    // the concrete half (lib/concrete.knowledge.ts) when the workspace runs it:
    // the rate cards' ranges and the FAQ, under the concrete brand's name
    const concrete = enabledVerticals(s, store.enabledLenses).includes("concrete")
      ? concreteKnowledge(s, brandOf({ company: store.company || s.company, settings: s }, "concrete")) : "";
    return [
      "BUSINESS KNOWLEDGE (answer with these ballparks; never reveal internal costs):",
      lines.length ? (concrete ? "FENCE — installed pricing (typical 150 ft job, 6 ft height):\n" : "Installed pricing (typical 150 ft job, 6 ft height):\n") + lines.join("\n") : "",
      "Exact pricing always comes from a free satellite measurement of the property — offer to book the free on-site estimate, or have a link to the instant-estimate tool texted over.",
      "Payments: secure online card/ACH. " + depTxt,
      finLive ? `Monthly financing is available (about ${fin.termMonths || 60}-month terms through ${fin.partner || "Wisetack"}) — mention it on larger jobs.` : "",
      s.companyCity ? `Service area: ${s.companyCity} and surrounding communities.` : "",
      "Process: instant satellite estimate → e-sign the quote online → pay the deposit online → install gets scheduled (weather permitting).",
      "If asked something not covered here (exact warranty terms, permits, HOA specifics), take the caller's name and number and say the owner will follow up personally.",
      concrete,
    ].filter(Boolean).join("\n");
  } catch (e) { return ""; }
}

/* "What the agent knows" — the composed knowledge, readable before it's synced,
   so a changed rate card or a missing coating price is seen here first. */
function KnowledgePreview({ store }: any) {
  const [open, setOpen] = React.useState<any>(false);
  const text = React.useMemo(() => (open ? buildAgentKnowledge(store) : ""), [open, store.settings, store.customProducts]);
  return (
    <details style={{ marginTop: 8 }} onToggle={(e: any) => setOpen(!!e.currentTarget.open)}>
      <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>What the agent knows</summary>
      {open && <pre className="faint" style={{ fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", margin: "8px 0 0", maxHeight: 320, overflow: "auto", fontFamily: "var(--font-mono)" }}>{text || "Nothing composed yet — add products or rate cards."}</pre>}
    </details>
  );
}

/* one call in the "Recent calls" list; a call that carries a transcript
   (the sandbox's recorded sample call — Retell calls will once the usage
   endpoint forwards them) expands to show it. */
function CallRow({ c }: any) {
  const [open, setOpen] = React.useState<any>(false);
  const hasT = Array.isArray(c.transcript) && c.transcript.length > 0;
  return (
    <div className={"ai-call" + (hasT ? " has-transcript" : "")} style={hasT ? { flexWrap: "wrap", cursor: "pointer" } : undefined} onClick={() => hasT && setOpen((o: boolean) => !o)} title={hasT ? (open ? "Hide the transcript" : "Read the transcript") : undefined}>
      <span className="ai-call-dot" style={{ background: c.outcome === "Booked estimate" ? "var(--green)" : "var(--teal)" }} />
      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}{c.demo && <span className="chip" style={{ fontSize: 9.5, padding: "0 6px", marginLeft: 6, color: "var(--amber)", borderColor: "var(--amber)" }}>SAMPLE</span>}</div><div className="muted" style={{ fontSize: 11.5 }}>{c.outcome} · {c.val}</div></div>
      <span className="faint" style={{ fontSize: 11 }}>{c.time}{hasT ? (open ? " ▴" : " ▾") : ""}</span>
      {hasT && open && (
        <div className="ai-transcript" onClick={(e) => e.stopPropagation()}>
          {c.transcript.map((t: any, i: number) => <div key={i} className={"ai-tline " + (t.who === "AI" ? "ai" : "caller")}><b>{t.who}</b><span>{t.text}</span></div>)}
          {c.recordingUrl ? <audio controls src={c.recordingUrl} style={{ width: "100%", marginTop: 8 }} /> : <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>Transcript of a sample call. Live calls include the recording.</div>}
        </div>
      )}
    </div>
  );
}

function AISuite({ store, go, params }: any) {
  const ai = (store.settings && store.settings.ai) || {};
  const recept = ai.receptionist || {};
  const autos = ai.automations || {};
  const cfgs = ai.automationCfg || {};
  const routing = ai.routing || {};
  const billing = ai.billing || {};
  const setAI = (patch) => store.setSettings({ ai: Object.assign({}, ai, patch) });
  const setRecept = (patch) => setAI({ receptionist: Object.assign({}, recept, patch) });
  const setAuto = (id, on) => { if ((AI_AUTOMATIONS.find(x => x.id === id) || {}).soon) return; setAI({ automations: Object.assign({}, autos, { [id]: on }) }); };
  const setCfg = (id, patch) => setAI({ automationCfg: Object.assign({}, cfgs, { [id]: Object.assign({}, cfgs[id], patch) }) });
  const setRouting = (patch) => setAI({ routing: Object.assign({}, routing, patch) });
  const setBilling = (patch) => setAI({ billing: Object.assign({}, billing, patch) });
  const gsConnected = !!(store.settings && store.settings.integrations && store.settings.integrations.gsheets && store.settings.integrations.gsheets.connected);
  const connectSheets = async () => {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.oauthStart) { notify("Connecting Google Sheets needs the live service (app.fenceworks.ai)."); return; }
    try { const url = await DB.oauthStart("gsheets"); if (url) window.location.href = url; }
    catch (e: any) { notify("Couldn't start Google Sheets connect: " + (e.message || e)); }
  };
  // one-click "make me a leads sheet" — creates it on the connected Google
  // account (header pre-seeded), fills the URL field, and opens the sheet
  const [sheetBusy, setSheetBusy] = React.useState<any>(false);
  const createSheet = async () => {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.gsheetsCreateSheet) { notify("Creating a sheet needs the live service (app.fenceworks.ai)."); return; }
    setSheetBusy(true);
    try {
      const co = (store.settings && store.settings.companyName) || "Fence Works";
      const r = await DB.gsheetsCreateSheet(co + " — Leads");
      if (r && r.url) { setRouting({ sheetUrl: r.url }); window.open(r.url, "_blank", "noopener"); }
    } catch (e: any) {
      const m = String((e && e.message) || e);
      notify(/not connected/i.test(m) ? "Connect Google Sheets first, then create the sheet." : "Couldn't create the sheet: " + m);
    } finally { setSheetBusy(false); }
  };
  // "soon" automations have no event source yet → never on, never counted active
  const isOn = (a) => a.soon ? false : (autos[a.id] != null ? autos[a.id] : a.default);

  const [tab, setTab] = React.useState<any>((params && params.section === "settings") ? "settings" : "agents");   // agents | settings
  // sync tab when navigated from the sidebar (Agents vs Settings) — params change while mounted
  React.useEffect(() => { setTab((params && params.section === "settings") ? "settings" : "agents"); }, [params && params.section]);
  const [openId, setOpenId] = React.useState<any>(null);   // automation detail drawer
  const [filter, setFilter] = React.useState<any>("all");  // group filter
  const [provBusy, setProvBusy] = React.useState<any>(false);   // Retell provisioning
  const [provErr, setProvErr] = React.useState<any>("");
  const [minsBlocked, setMinsBlocked] = React.useState<any>(false);  // out of AI talk-minutes → block activation
  // credit/usage runtime (window.VFP_CREDITS, separate PR — feature-detected, no
  // hard dependency). When present, st reflects the REAL plan/trial allowances.
  const cr: any = (window as any).VFP_CREDITS;
  const st: any = cr && cr.creditState && cr.creditState(store);
  // stand up the real Retell agent + tracked number (saves settings first so the
  // server reads the chosen voice/greeting), then reflect the issued number.
  async function activateReceptionist() {
    const DB: any = window.VFP_DB;
    // out of AI talk-minutes → block activation & surface the upsell (the server
    // enforces the hard cutoff too; this just keeps a dead line from spinning up).
    if (st && st.minLeft <= 0) { setMinsBlocked(true); return; }
    if (!DB || !DB.retellProvision || !DB.apiBase || !DB.apiBase()) { setProvErr("Live phone service isn't configured on this deployment yet."); return; }
    setProvErr(""); setProvBusy(true);
    try {
      if (DB.flushNow) await DB.flushNow();
      const r = await DB.retellProvision({ areaCode: recept.areaCode, agentVoice: recept.voice, knowledge: buildAgentKnowledge(store) });
      if (r && r.number) { setRecept({ trackedNumber: r.number, agentProvisioned: true }); store.addActivity("AI receptionist activated · " + r.number); }
    } catch (e: any) { setProvErr(String((e && e.message) || e)); }
    finally { setProvBusy(false); }
  }
  // re-push pricing/offers/FAQ into the live agent (after price-book changes)
  const [kbBusy, setKbBusy] = React.useState<any>(false);
  const [kbDone, setKbDone] = React.useState<any>(false);
  async function syncKnowledge() {
    const DB: any = window.VFP_DB;
    if (!DB || !DB.retellSyncKnowledge) { notify("Knowledge sync needs the live service (app.fenceworks.ai)."); return; }
    setKbBusy(true); setKbDone(false);
    try { if (DB.flushNow) await DB.flushNow(); await DB.retellSyncKnowledge(buildAgentKnowledge(store)); setKbDone(true); store.addActivity("Synced pricing & FAQ to the AI receptionist"); }
    catch (e: any) { notify("Couldn't sync knowledge: " + ((e && e.message) || e)); }
    finally { setKbBusy(false); }
  }
  // release the number + delete the Retell agent/LLM so it stops billing
  async function deactivateReceptionist() {
    const DB: any = window.VFP_DB;
    if (!DB || !DB.disconnectIntegration) return;
    if (!(await confirmDialog({ title: "Release your AI phone number?", body: "Callers can no longer reach the AI on it, and the number can't be recovered — re-activating later buys a NEW number (carrier fee ~$2, and new digits if you've handed these out). Your agent setup and synced pricing/FAQ are kept.", confirmLabel: "Release number", danger: true }))) return;
    setProvErr(""); setProvBusy(true);
    try {
      if (DB.flushNow) await DB.flushNow();
      await DB.disconnectIntegration("retell");
      setRecept({ trackedNumber: "", agentProvisioned: false });
      store.addActivity("AI receptionist deactivated · number released");
    } catch (e: any) { setProvErr(String((e && e.message) || e)); }
    finally { setProvBusy(false); }
  }

  const live = recept.enabled;
  // which services this workspace runs, and the concrete brand the agent answers as
  const runsConcrete = enabledVerticals(store.settings, store.enabledLenses).includes("concrete");
  const concreteBrand = brandOf({ company: store.company || (store.settings && store.settings.company), settings: store.settings }, "concrete");
  // the concrete brand's own line (ai_lines; §13 item 25): its row, add one, release its number
  const [lines, setLines] = React.useState<any[]>([]);
  const [lineArea, setLineArea] = React.useState<any>("");
  const [lineBusy, setLineBusy] = React.useState<any>(false);
  const [lineErr, setLineErr] = React.useState<any>("");
  const concreteLine = lines.find((l: any) => l && l.vertical === "concrete") || null;
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!runsConcrete || !DB || !DB.aiLines) return;
    let dead = false;
    DB.aiLines().then((rows: any[]) => { if (!dead) setLines(Array.isArray(rows) ? rows : []); }).catch(() => {});
    return () => { dead = true; };
  }, [runsConcrete]);
  async function addConcreteLine() {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.retellProvisionLine || !DB.apiBase || !DB.apiBase()) { setLineErr("Live phone service isn't configured on this deployment yet."); return; }
    if (!concreteBrand.name) { setLineErr("Name the concrete brand under Settings → Services first — the line answers as that name."); return; }
    setLineErr(""); setLineBusy(true);
    try {
      if (DB.flushNow) await DB.flushNow();
      const r = await DB.retellProvisionLine({ vertical: "concrete", areaCode: lineArea || recept.areaCode || undefined, agentVoice: recept.voice, knowledge: buildAgentKnowledge(store) });
      if (r && r.number) { setLines(await DB.aiLines()); store.addActivity(concreteBrand.name + " line activated · " + r.number); }
    } catch (e: any) { setLineErr(String((e && e.message) || e)); }
    finally { setLineBusy(false); }
  }
  async function releaseConcreteLine() {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.retellReleaseLine) return;
    if (!(await confirmDialog({ title: "Release the " + (concreteBrand.name || "concrete") + " number?", body: "Callers can no longer reach the AI on it, and the number can't be recovered — adding a line later buys a new number. The agent stays, so it keeps its words and knowledge.", confirmLabel: "Release number", danger: true }))) return;
    setLineErr(""); setLineBusy(true);
    try { await DB.retellReleaseLine("concrete"); setLines(await DB.aiLines()); store.addActivity((concreteBrand.name || "Concrete") + " line released"); }
    catch (e: any) { setLineErr(String((e && e.message) || e)); }
    finally { setLineBusy(false); }
  }
  const CARDS = cardsFor(store.lens);   // 811 is not a concrete card
  const activeAutos = CARDS.filter(isOn).length;
  // live usage straight from Retell (month-to-date minutes + recent calls) —
  // hydrates the counters/log with REAL data, including past calls
  const [liveUsage, setLiveUsage] = React.useState<any>(null);
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.retellUsage || !recept.trackedNumber) return;
    let on = true;
    DB.retellUsage().then((u: any) => { if (on && u && Array.isArray(u.calls)) setLiveUsage(u); }).catch(() => {});
    return () => { on = false; };
  }, [recept.trackedNumber]);
  // real calls only — no fabricated log. Populated from recept.calls once the AI
  // line starts taking calls; empty until then (honest empty state in the panel).
  const callLog = (liveUsage && liveUsage.calls.length) ? liveUsage.calls : (recept.enabled && Array.isArray(recept.calls) ? recept.calls : []);
  const groups = [...new Set(CARDS.map(a => a.group))];
  const openAuto = CARDS.find(a => a.id === openId);

  // ----- minutes / usage (Pro = 90 included) -----
  // Prefer the credit runtime's talk-minute allowance when present so a trial
  // sees its reduced cap; otherwise fall back to the existing plan/billing values.
  const included = st ? st.minTotal : (billing.includedMin != null ? billing.includedMin : 90);
  const used = st ? st.minUsed : (liveUsage ? liveUsage.usedMin : (billing.usedMin != null ? billing.usedMin : 0));
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  const TABS = [["agents", "Agents"], ["settings", "Settings"]];

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="kicker kicker-teal">Fence Works AI</span>
          <h1 className="display" style={{ fontSize: 28, margin: "6px 0 4px" }}>AI Front Desk</h1>
          <p className="muted" style={{ margin: 0 }}>Answer every call, capture every lead, and let routine follow-up run itself.</p>
        </div>
        <div className="row gap-8" style={{ alignSelf: "flex-start", alignItems: "center", flexWrap: "wrap" }}>
          {/* credits indicator — only when the credit runtime (separate PR) is present */}
          {st && <span className="muted" style={{ fontSize: 12 }}>{cr.fmtCredits(st.creditsLeft)} credits left this period</span>}
          {st && st.trial && <span className="chip" style={{ fontSize: 10.5 }}>Trial</span>}
          <span className="chip teal">Pro · add-on</span>
        </div>
      </div>

      <div className="cat-tabs" style={{ marginBottom: 18 }}>
        {TABS.map(([id, lb]) => <button key={id} className={"filter-chip" + (tab === id ? " on" : "")} onClick={() => setTab(id)}>{lb}</button>)}
      </div>

      {/* ================= AGENTS ================= */}
      {tab === "agents" && (
        <>
          <div className="ai-hero">
            <div className="ai-hero-main">
              <div className="row gap-10" style={{ marginBottom: 6 }}>
                <span className="ai-phone-ic"><Icon name="phone" size={20} /></span>
                <div>
                  <div className="display" style={{ fontSize: 19 }}>AI Call Receptionist</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>Never miss a call — even after hours or when you're on a job.</div>
                </div>
                <label className="toggle-row" style={{ width: "auto", marginLeft: "auto", border: 0, background: "none", padding: 0 }} onClick={() => setRecept({ enabled: !recept.enabled })}>
                  <span className={"sw" + (recept.enabled ? " on" : "")}><span className="knob" /></span>
                </label>
              </div>
              <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, margin: "10px 0 16px" }}>
                A natural-voice agent answers your business line, qualifies the caller ({runsConcrete ? "fence or concrete, address, timeline" : "fence type, address, timeline"}), books an on-site estimate straight into your <b style={{ color: "var(--fg)" }}>Schedule</b>, and drops a lead into <b style={{ color: "var(--fg)" }}>Customers</b>. If it can't close, it texts your instant-estimate {runsConcrete ? "link — the concrete estimate form on a concrete call" : "widget link"}.
                {runsConcrete && <> Concrete calls are answered as <b style={{ color: "var(--fg)" }}>{concreteBrand.name}</b>{concreteBrand.isCompany ? "" : " (same line; a second number for that brand is the next step)"}.</>}
              </p>
              {recept.enabled && (
                <div className="ai-config">
                  {/* Who rings first, business hours and what happens when nobody picks up
                      live on the Business line screen (routing on the number itself). The
                      old "Forwarding number" / "Answer after" fields here were stored and
                      never read (LAUNCH_STATUS §1). */}
                  <div className="pe-callout info" style={{ margin: "0 0 10px" }}>
                    <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Who rings first, your hours, and whether a missed call goes to this receptionist or to voicemail are set on your <a href="#/phone" onClick={(e) => { e.preventDefault(); go("phone"); }} style={{ fontWeight: 700 }}>Business line</a>. Forwarding your existing number at your carrier still works too.</p>
                  </div>
                  <div className="ai-field"><label className="field-label">Voice engine</label>
                    <select className="select" value={recept.voiceEngine || "s2s_mini"} onChange={e => setRecept({ voiceEngine: e.target.value })}>
                      <option value="s2s_mini">Speech-to-speech · GPT Realtime mini (~$0.13/min)</option>
                      <option value="cascading">Cascading (~$0.115/min)</option>
                      <option value="realtime">GPT Realtime 1.5 (~$0.40/min)</option>
                    </select></div>
                  <div className="ai-field"><label className="field-label">Agent voice</label>
                    <select className="select" value={recept.voice || "warm-f"} onChange={e => setRecept({ voice: e.target.value })}>
                      <option value="warm-f">Warm — female</option><option value="warm-m">Warm — male</option><option value="pro-f">Professional — female</option><option value="pro-m">Professional — male</option>
                    </select></div>
                  <div className="ai-field"><label className="field-label">Greeting</label><input className="input" value={recept.greeting || ""} placeholder="Thanks for calling — how can we help?" onChange={e => setRecept({ greeting: e.target.value })} /></div>
                  {/* FW-013 / L08: it captures a REQUEST. Nothing checks your
                      availability or holds the slot — the request lands on the
                      job as "confirm it", and you confirm. The old label said
                      "book estimates on my calendar", which it never did. */}
                  <label className="toggle-row" onClick={() => setRecept({ canBook: !(recept.canBook !== false) })}><span className={"sw" + (recept.canBook !== false ? " on" : "")}><span className="knob" /></span><span style={{ fontSize: 13, fontWeight: 600 }}>Let it ask callers for a preferred estimate time</span></label>
                  <p className="faint" style={{ fontSize: 11.5, margin: "-4px 0 0 44px" }}>It records what they ask for and puts it on the job for you to confirm — it doesn't check your calendar or hold the slot.</p>
                  {/* provision the real AI line */}
                  <div className="ai-field" style={{ borderTop: "1px solid var(--line)", paddingTop: 12, marginTop: 4 }}>
                    {recept.trackedNumber ? (
                      <div className="pe-callout info" style={{ margin: 0 }}>
                        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>📞 Your AI line is live at <b style={{ color: "var(--teal)" }}>{recept.trackedNumber}</b>. <b style={{ color: "var(--fg-2)" }}>Forward your business number to it</b> (or hand it out directly) and it answers, qualifies callers, and captures estimate requests for you to confirm.</p>
                        <div className="row gap-8" style={{ marginTop: 8, flexWrap: "wrap" }}>
                          <button className="btn btn-outline btn-sm" disabled={kbBusy} onClick={syncKnowledge} title="Push your current price book, deposit policy, financing & FAQ into the agent so it answers pricing questions with your real numbers"><Icon name="bolt" size={13} />{kbBusy ? "Syncing…" : kbDone ? "Synced ✓" : "Sync pricing & FAQ to agent"}</button>
                          <button className="btn btn-ghost btn-sm" disabled={provBusy} onClick={deactivateReceptionist}><Icon name="trash" size={13} />{provBusy ? "Releasing…" : "Release number & deactivate"}</button>
                        </div>
                        <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>The agent learns your products' installed $/ft, gates, deposit policy &amp; financing{runsConcrete ? ", and the concrete rate cards' ranges, cure times and FAQ" : ""}. Re-sync after you change pricing.</p>
                        <KnowledgePreview store={store} />
                        {provErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: "6px 0 0" }}>{provErr}</p>}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {/* out of AI talk-minutes → block activation with an upsell */}
                        {(minsBlocked || (st && st.minLeft <= 0)) ? (
                          <div className="pe-callout" style={{ margin: 0, borderColor: "var(--amber-tint)" }}>
                            <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--fg-2)" }}>You're out of AI talk-minutes — upgrade to keep the receptionist on.</p>
                            <button className="btn btn-primary btn-sm" onClick={() => go("account", { tab: "plan" })}><Icon name="bolt" size={13} />Upgrade plan</button>
                          </div>
                        ) : (<>
                        {/* trial preview — capped allowance; the server enforces the hard cutoff */}
                        {st && st.trial && (
                          <div className="pe-callout info" style={{ margin: 0 }}>
                            <p style={{ margin: 0, fontSize: 12.5, color: "var(--fg-2)" }}>Trial preview — <b>{cr.fmtCredits(st.minLeft)}</b> AI talk-minutes included. <a onClick={() => go("account", { tab: "plan" })} style={{ color: "var(--teal)", cursor: "pointer" }}>Upgrade</a> for the full allowance.</p>
                          </div>
                        )}
                        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
                          <div style={{ width: 130 }}><label className="field-label">Area code</label><input className="input" value={recept.areaCode || ""} placeholder="920" onChange={e => setRecept({ areaCode: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></div>
                          <button className="btn btn-primary btn-sm" disabled={provBusy} onClick={activateReceptionist}><Icon name="phone" size={14} />{provBusy ? "Provisioning…" : "Activate AI line — get a number"}</button>
                        </div>
                        <p className="faint" style={{ fontSize: 11, margin: 0 }}>We provision a dedicated number and your AI agent. Then forward your business line to it.</p>
                        </>)}
                        {provErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: 0 }}>{provErr}</p>}
                      </div>
                    )}
                  </div>
                  {/* the concrete brand's own line (§13 item 25): answered as Rise & Shine on its own number */}
                  {runsConcrete && (
                    <div className="ai-field" style={{ borderTop: "1px solid var(--line)", paddingTop: 12, marginTop: 4 }} data-tour="ai-concrete-line">
                      {concreteLine && concreteLine.tracked_number ? (
                        <div className="pe-callout info" style={{ margin: 0 }}>
                          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>📞 <b style={{ color: "var(--fg-2)" }}>{concreteBrand.name || "Concrete"}</b> answers at <b style={{ color: "var(--teal)" }}>{concreteLine.tracked_number}</b> — the same receptionist, under that name. Put this number on the concrete site, the yard signs and the door hangers.</p>
                          <div className="row gap-8" style={{ marginTop: 8, flexWrap: "wrap" }}>
                            <button className="btn btn-ghost btn-sm" disabled={lineBusy} onClick={releaseConcreteLine}><Icon name="trash" size={13} />{lineBusy ? "Releasing…" : "Release this number"}</button>
                          </div>
                          {lineErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: "6px 0 0" }}>{lineErr}</p>}
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 8 }}>
                          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div style={{ width: 130 }}><label className="field-label">Area code</label><input className="input" value={lineArea || recept.areaCode || ""} placeholder="920" onChange={e => setLineArea(e.target.value)} /></div>
                            <button className="btn btn-outline btn-sm" disabled={lineBusy || !recept.trackedNumber} title={recept.trackedNumber ? "" : "Activate the main AI line first"} onClick={addConcreteLine}><Icon name="phone" size={14} />{lineBusy ? "Provisioning…" : `Add a line for ${concreteBrand.name || "the concrete brand"}`}</button>
                          </div>
                          <p className="faint" style={{ fontSize: 11, margin: 0 }}>A second number the same receptionist answers as <b>{concreteBrand.name || "the concrete brand"}</b> — callers on it land in the Concrete lens. It takes talk-minutes from the same plan.</p>
                          {lineErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: 0 }}>{lineErr}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="ai-hero-side">
              <div className="kicker" style={{ marginBottom: 10 }}>{live ? "Recent calls" : "What you're missing"}</div>
              {live ? (
                <div style={{ display: "grid", gap: 9 }}>
                  {callLog.length === 0 && <div className="muted" style={{ fontSize: 12.5, padding: "8px 2px", lineHeight: 1.5 }}>No calls yet — your AI line's calls show up here once it starts taking them.</div>}
                  {callLog.map((c, i) => (
                    <CallRow key={i} c={c} />
                  ))}
                  <div className="ai-usage">
                    <div className="row between" style={{ fontSize: 12, marginBottom: 5 }}><span className="muted">Talk minutes</span><span className="tnum"><b>{used}</b> / {included} min</span></div>
                    <div className="ai-bar"><div style={{ width: pct + "%", background: pct > 90 ? "var(--amber)" : "var(--teal)" }} /></div>
                    <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 9 }} onClick={() => setTab("settings")}><Icon name="sliders" size={13} />Usage &amp; billing</button>
                  </div>
                </div>
              ) : (
                <div className="ai-miss">
                  <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--fg2)", lineHeight: 1.6 }}>Contractors miss <b style={{ color: "var(--fg)" }}>~30% of inbound calls</b> — and <b style={{ color: "var(--fg)" }}>85% of callers</b> who hit voicemail never call back. That's booked jobs going to whoever answers first.</p>
                  <button className="btn btn-primary btn-block" onClick={() => setRecept({ enabled: true })}><Icon name="phone" size={15} />Turn on the AI receptionist</button>
                </div>
              )}
            </div>
          </div>
          <div className="ai-quick">
            <button className="ai-quick-card" onClick={() => setTab("settings")}><Icon name="sliders" size={18} /><div><div style={{ fontWeight: 700, fontSize: 14 }}>Lead routing &amp; billing</div><div className="muted" style={{ fontSize: 12 }}>Where leads go · {used}/{included} min used →</div></div></button>
            {/* FW-026: this said "Calls, SMS & email are built in — nothing to
                connect" while smsReady() refuses to send until the workspace's
                A2P 10DLC registration is approved. A contractor read that,
                switched on an automation that texts customers, and watched
                nothing arrive — with no reason given, because this card had
                already told them it was handled. Carrier registration is a
                real multi-day process and the card says so until it's done. */}
            <ChannelsCard go={go} />
          </div>
          <div className="row between" style={{ margin: "28px 0 14px", flexWrap: "wrap", gap: 8 }}>
            <h2 className="display" style={{ fontSize: 19, margin: 0 }}>Automation agents</h2>
            <span className="muted" style={{ fontSize: 13 }}>{activeAutos} of {AI_AUTOMATIONS.length} active</span>
          </div>
        </>
      )}

      {/* ================= AUTOMATIONS (under Agents) ================= */}
      {tab === "agents" && (
        <>
          <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div className="cat-tabs" style={{ margin: 0 }}>
              <button className={"filter-chip" + (filter === "all" ? " on" : "")} onClick={() => setFilter("all")}>All · {CARDS.length}</button>
              <button className={"filter-chip" + (filter === "active" ? " on" : "")} onClick={() => setFilter("active")}>Active · {activeAutos}</button>
              <button className={"filter-chip" + (filter === "star" ? " on" : "")} onClick={() => setFilter("star")}>★ Recommended</button>
              {groups.map(g => <button key={g} className={"filter-chip" + (filter === g ? " on" : "")} onClick={() => setFilter(g)}>{g}</button>)}
            </div>
          </div>
          {CARDS.filter(a => (filter === "all" || filter === "active" || filter === "star" || a.group === filter) && (filter !== "active" || isOn(a)) && (filter !== "star" || a.star)).length === 0 && (
            <div className="card pad"><EmptyState icon="bolt" title={filter === "active" ? "No active automations" : "Nothing here yet"} sub={filter === "active" ? "Switch one on from any automation and it'll show up here." : "Pick a different filter to see the automations in this view."} /></div>
          )}
          {groups.filter(g => filter === "all" || filter === "active" || filter === "star" || filter === g).map(g => {
            const items = CARDS.filter(a => a.group === g && (filter !== "active" || isOn(a)) && (filter !== "star" || a.star));
            if (!items.length) return null;
            return (
              <div key={g} style={{ marginBottom: 18 }}>
                <div className="kicker" style={{ marginBottom: 10 }}>{g}</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {items.map(a => {
                    const on = isOn(a);
                    return (
                      <ListRow key={a.id} className={on ? "on" : ""} muted={!!a.soon} onClick={() => go("automation", { autoId: a.id })}
                        leading={a.soon
                          ? <span className="ai-auto-ic" title="Coming soon"><Icon name={a.icon} size={15} /></span>
                          : <button type="button" className={"sw" + (on ? " on" : "")} role="switch" aria-checked={on} aria-label={(on ? "Turn off " : "Turn on ") + a.name} onClick={() => setAuto(a.id, !on)}><span className="knob" /></button>}
                        title={<><span>{a.name}</span>{a.star && <span className="ai-star" title="High-priority — recommended">★</span>}<span className="muted" style={{ fontWeight: 500, fontSize: 12 }}>{a.group}</span></>}
                        sub={a.blurb}
                        trailing={<>
                          <ChanChip a={a} />
                          {a.soon
                            ? <span className="chip" style={{ fontSize: 10.5 }}>Coming soon</span>
                            : on
                              ? <span className="chip green" style={{ fontSize: 10.5 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: "var(--green)" }} />Active</span>
                              : <span className="chip" style={{ fontSize: 10.5 }}>Paused</span>}
                          <button className="ai-manage" style={{ background: "none", border: 0, cursor: "pointer", padding: "4px 0" }} onClick={() => go("automation", { autoId: a.id })}>Manage <Icon name="chevright" size={12} /></button>
                        </>} />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ================= SETTINGS ================= */}
      {tab === "settings" && (
        <div className="ai-settings">
          {/* lead routing */}
          <div className="card pad">
            <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 6 }}><Icon name="arrow" size={14} />Lead Routing</div>
            <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Where should captured leads (calls, web chat, widget) land?</p>
            <div style={{ display: "grid", gap: 8 }}>
              {[["estimates", "clipboard", "Into Estimates — To Review", "New leads become draft estimates in a 'To Review' queue you can quote from."],
                ["sheet", "box", "Google Sheet", "Append every lead as a row to a shared sheet — simplest hand-off."],
                ["crm", "send", "3rd-party CRM webhook", "POST each lead to HubSpot, Jobber, GoHighLevel, etc. via webhook URL."]].map(([id, ic, t, d]) => {
                const on = (routing.dest || "estimates") === id;
                return (
                  <button key={id} className={"ai-route" + (on ? " on" : "")} onClick={() => setRouting({ dest: id })}>
                    <span className={"radio" + (on ? " on" : "")}>{on && <span className="dot2" />}</span>
                    <span className="ai-route-ic"><Icon name={ic} size={15} /></span>
                    <span style={{ flex: 1 }}><span style={{ fontWeight: 700, fontSize: 13.5, display: "block" }}>{t}</span><span className="muted" style={{ fontSize: 12 }}>{d}</span></span>
                  </button>
                );
              })}
            </div>
            {(routing.dest || "estimates") === "sheet" && <div style={{ marginTop: 12 }}>
              <label className="field-label">Google Sheet URL</label>
              <input className="input" value={routing.sheetUrl || ""} placeholder="https://docs.google.com/spreadsheets/d/…" onChange={e => setRouting({ sheetUrl: e.target.value })} />
              <div className="row gap-8" style={{ marginTop: 8, flexWrap: "wrap" }}>
                {gsConnected
                  ? <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={11} />&nbsp;Google Sheets connected</span>
                  : <button className="btn btn-outline btn-sm" onClick={connectSheets}><Icon name="box" size={13} />&nbsp;Connect Google Sheets</button>}
                <button className="btn btn-outline btn-sm" disabled={sheetBusy} onClick={createSheet}><Icon name="plus" size={13} />&nbsp;{sheetBusy ? "Creating…" : "Create a new sheet"}</button>
              </div>
              <p className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>Paste an existing sheet's URL — or hit "Create a new sheet" and we'll make a fresh one on your connected Google account, fill the URL in, and open it. We append a row per lead — date, name, phone, email, address, estimate $, source (header row included).</p>
            </div>}
            {(routing.dest) === "crm" && <div style={{ marginTop: 12 }}><label className="field-label">CRM webhook URL</label><input className="input" value={routing.webhookUrl || ""} placeholder="https://hooks.yourcrm.com/leads/…" onChange={e => setRouting({ webhookUrl: e.target.value })} /></div>}
            <label className="toggle-row" style={{ marginTop: 12 }} onClick={() => setRouting({ alsoEmail: !routing.alsoEmail })}><span className={"sw" + (routing.alsoEmail ? " on" : "")}><span className="knob" /></span><span style={{ fontSize: 13, fontWeight: 600 }}>Also email me every new lead</span></label>
          </div>

          {/* billing / minutes */}
          <div className="card pad">
            <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 6 }}><Icon name="dollar" size={14} />Talk Minutes &amp; Billing</div>
            <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Your Pro plan includes <b style={{ color: "var(--fg)" }}>{included} AI talk-minutes/mo</b>. Set how overage is handled.</p>
            <div className="ai-usage" style={{ marginBottom: 16 }}>
              <div className="row between" style={{ fontSize: 13, marginBottom: 6 }}><span className="muted">This cycle</span><span className="tnum"><b style={{ color: pct > 90 ? "var(--amber)" : "var(--teal)" }}>{used}</b> / {included} min · resets in 12d</span></div>
              <div className="ai-bar"><div style={{ width: pct + "%", background: pct > 90 ? "var(--amber)" : "var(--teal)" }} /></div>
            </div>
            <label className="toggle-row" onClick={() => setBilling({ overage: !billing.overage })}><span className={"sw" + (billing.overage ? " on" : "")}><span className="knob" /></span><span style={{ fontSize: 13, fontWeight: 600 }}>Allow overage</span><span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>$0.18 / extra min</span></label>
            {billing.overage ? (
              <>
                <div style={{ marginTop: 12 }}>
                  <label className="field-label">Monthly overage cap</label>
                  <div className="row gap-8">
                    {[20, 50, 100].map(v => <button key={v} className={"filter-chip" + ((billing.cap || 50) === v ? " on" : "")} onClick={() => setBilling({ cap: v })}>${v}</button>)}
                    <button className={"filter-chip" + (billing.cap === 0 ? " on" : "")} onClick={() => setBilling({ cap: 0 })}>No cap</button>
                  </div>
                  <p className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>At the cap, the agent keeps taking messages but stops booking calls until next cycle.</p>
                </div>
                <label className="toggle-row" style={{ marginTop: 12 }} onClick={() => setBilling({ autobill: !(billing.autobill !== false) })}><span className={"sw" + (billing.autobill !== false ? " on" : "")}><span className="knob" /></span><span style={{ fontSize: 13, fontWeight: 600 }}>Auto-bill overage to card on file</span></label>
              </>
            ) : (
              <div className="pe-callout info" style={{ marginTop: 12 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Overage off: at {included} min the agent switches to take-a-message mode until your cycle resets. Turn on overage to keep booking.</p></div>
            )}
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 14 }} onClick={() => go("account")}><Icon name="bolt" size={13} />Need more? Upgrade plan</button>
          </div>
        </div>
      )}

      <div className="pe-callout info" style={{ marginTop: 18 }}>
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}><b style={{ color: "var(--fg-2)" }}>Setup note.</b> Telephony, SMS &amp; email delivery are provisioned by Fence Works — there's nothing for you to connect. Toggles here are live in-app; your forwarding number is issued when the receptionist is activated.</p>
      </div>
    </div>
  );
}

/* ============================================================
   Manage screen — controls are driven by a.channelType, and write
   to the exact config keys each run(ctx) reads in automations.js.
   ============================================================ */

/* ---- readiness ----------------------------------------------------------
   One fetch, shared by every automation screen: the workspace's A2P 10DLC
   state plus each automation's unmet prerequisites. Cached for the session so
   opening five automations doesn't hit the API five times. */
let _readyCache: any = null;
let _readyAt = 0;
function useReadiness() {
  const [r, setR] = React.useState<any>(_readyCache);
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.automationReadiness || !DB.apiBase || !DB.apiBase()) return;
    if (_readyCache && Date.now() - _readyAt < 60_000) { setR(_readyCache); return; }
    let alive = true;
    DB.automationReadiness().then((d: any) => {
      if (!d || !alive) return;
      _readyCache = d; _readyAt = Date.now(); setR(d);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return r;
}
// invalidate after an action that could satisfy a prerequisite
function refreshReadiness() { _readyCache = null; _readyAt = 0; }

/* FW-026 — what is actually connected, said honestly.
   Calls and email ARE built in. Texting is not: US carriers require A2P 10DLC
   brand + campaign registration before they will deliver business texts, and
   smsReady() refuses to send until it's approved. Telling a contractor there's
   "nothing to connect" is what turns a known multi-day wait into a mystery. */
function ChannelsCard({ go }: any) {
  const ready = useReadiness();
  const sms = ready && ready.sms;
  const pending = sms && !sms.ready;
  return (
    <button className="ai-quick-card" onClick={() => go("ai", { section: "settings" })}>
      <Icon name={pending ? "bolt" : "check"} size={18} style={pending ? { color: "var(--amber)" } : undefined} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{pending ? "Channels — texting needs registration" : "Channels — provisioned"}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {pending
            ? (sms.reason || "Calls & email are live. Texting waits on carrier approval") + " →"
            : "Calls, SMS & email are live — nothing to connect →"}
        </div>
      </div>
    </button>
  );
}

/* shared SMS / Email / Both control — appears ONLY on channelType "message".

   SMS is LOCKED until the workspace's A2P 10DLC registration is approved. US
   carriers drop unregistered business texts (error 30034) while Twilio still
   reports them "queued", so an unlocked SMS chip would let a contractor pick a
   channel whose every message silently vanishes. The lock names the real
   status and links to the registration. */
function SendChannel({ value, onChange, sms, go }: any) {
  const smsReady = !sms || sms.ready;   // no readiness data yet → don't block the UI
  const cur = value || (smsReady ? "sms" : "email");
  const opts: any[] = [["sms", "SMS"], ["email", "Email"], ["both", "Both"]];
  return (
    <div className="ai-field" style={{ marginBottom: 14 }}>
      <label className="field-label">Send channel</label>
      <div className="row gap-8">
        {opts.map(([v, lb]) => {
          const locked = !smsReady && (v === "sms" || v === "both");
          return (
            <button
              key={v}
              className={"filter-chip" + (cur === v ? " on" : "")}
              onClick={() => { if (!locked) onChange(v); }}
              disabled={locked}
              title={locked ? sms.reason : undefined}
              style={locked ? { opacity: .45, cursor: "not-allowed" } : undefined}
            >
              {locked && <Icon name="lock" size={11} style={{ marginRight: 4 }} />}{lb}
            </button>
          );
        })}
      </div>
      {!smsReady && (
        <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0", lineHeight: 1.5 }}>
          <b style={{ color: "var(--amber)" }}>SMS locked.</b> {sms.reason}{" "}
          {go && <a onClick={() => go("messages", { tab: "texts" })} style={{ color: "var(--teal)", cursor: "pointer" }}>
            {sms.status === "pending" ? "Check status" : "Set up texting"} →
          </a>}
        </p>
      )}
    </div>
  );
}

/* "This can't run yet, and here's the one thing that fixes it." Rendered from
   /automations/readiness so a contractor never switches something On and
   watches it do nothing. */
function Prereq({ need, go }: any) {
  if (!need || need.ok) return null;
  return (
    <div className="pe-callout info" style={{ marginBottom: 14, borderColor: "var(--amber-tint, var(--line))" }}>
      <div className="row gap-8" style={{ alignItems: "flex-start" }}>
        <Icon name="bolt" size={15} style={{ color: "var(--amber)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, fontWeight: 600, color: "var(--fg-2)" }}>Condition not met</p>
          <p className="muted" style={{ margin: "3px 0 0", fontSize: 12.5, lineHeight: 1.55 }}>{need.reason}</p>
          {need.fix && need.fix.page && go && (
            <button className="btn btn-outline btn-sm" style={{ marginTop: 9 }}
              onClick={() => { refreshReadiness(); go(String(need.fix.page).split("#")[0]); }}>
              <Icon name="arrow" size={12} />{need.fix.label || "Fix this"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
function MiniToggle({ on, onClick, label, note }: any) {
  return (
    <label className="toggle-row" style={{ marginTop: 12 }} onClick={onClick}>
      <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      {note && <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>{note}</span>}
    </label>
  );
}
function FieldSelect({ label, value, onChange, options }: any) {
  return <div className="ai-field"><label className="field-label">{label}</label>
    <select className="select" value={value} onChange={e => onChange(e.target.value)}>{options.map(([v, lb]) => <option key={v} value={v}>{lb}</option>)}</select></div>;
}
function Slider({ label, value, min, max, step, suffix, onChange }: any) {
  return <div className="ai-field"><label className="field-label" style={{ display: "flex", justifyContent: "space-between" }}>{label}<b style={{ color: "var(--teal)" }}>{value}{suffix}</b></label>
    <input type="range" min={min} max={max} step={step || 1} value={value} onChange={e => onChange(+e.target.value)} style={{ width: "100%" }} /></div>;
}
function TagList({ label, tags, onChange, placeholder }: any) {
  const [v, setV] = React.useState<any>("");
  const add = () => { const t = v.trim(); if (t && !tags.includes(t)) onChange([...tags, t]); setV(""); };
  return (
    <div className="ai-field"><label className="field-label">{label}</label>
      <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        {tags.map(t => <span key={t} className="chip" style={{ cursor: "default" }}>{t}<span style={{ marginLeft: 6, cursor: "pointer", opacity: .6 }} onClick={() => onChange(tags.filter(x => x !== t))}>×</span></span>)}
        {!tags.length && <span className="faint" style={{ fontSize: 12 }}>None yet</span>}
      </div>
      <div className="row gap-8">
        <input className="input" value={v} placeholder={placeholder} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button className="btn btn-ghost btn-sm" onClick={add}><Icon name="plus" size={13} />Add</button>
      </div>
    </div>
  );
}
function RuleRows({ rules, onChange }: any) {
  const upd = (i, k, val) => onChange(rules.map((r, j) => j === i ? { ...r, [k]: val } : r));
  return (
    <div className="ai-field">
      <label className="field-label">Rules — when → then</label>
      <div style={{ display: "grid", gap: 8 }}>
        {rules.map((r, i) => (
          <div key={i} className="row gap-8" style={{ alignItems: "center" }}>
            <input className="input" style={{ flex: 1 }} value={r.when} placeholder="When… (e.g. 2-car garage)" onChange={e => upd(i, "when", e.target.value)} />
            <Icon name="arrow" size={14} style={{ color: "var(--fg-3)", flexShrink: 0 }} />
            <input className="input" style={{ flex: 1 }} value={r.then} placeholder="Suggest… (e.g. epoxy floor)" onChange={e => upd(i, "then", e.target.value)} />
            <button className="icon-btn" title="Remove" onClick={() => onChange(rules.filter((_, j) => j !== i))} style={{ flexShrink: 0 }}><Icon name="trash" size={13} /></button>
          </div>
        ))}
      </div>
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onChange([...rules, { when: "", then: "" }])}><Icon name="plus" size={13} />Add rule</button>
    </div>
  );
}

const QUIET_OPTS = [["No quiet hours", "None — anytime"], ["8am – 8pm only", "8am – 8pm only"], ["9am – 6pm only", "9am – 6pm only"], ["custom", "Custom hours…"]];
// custom quiet hours — state quiet-hour laws differ (federal TCPA: 8am–9pm local)
const QH_HOURS = Array.from({ length: 24 }, (_, h) => [String(h), h === 0 ? "12am" : h < 12 ? h + "am" : h === 12 ? "12pm" : (h - 12) + "pm"]);
const DELAY_OPTS = [["immediate", "Immediately"], ["15m", "After 15 min"], ["1h", "After 1 hour"], ["1d", "Next day"]];

/* "Send myself a test" — fires the automation's template (sample data) at the
   OWNER's phone/email through the real send pipe; quiet hours/delay skipped. */
function TestSendRow({ a, cfg }: any) {
  const [st, setSt] = React.useState<any>(null);   // null | "sending" | {ok,msg} | {err}
  async function test() {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.automationTest || !DB.apiBase || !DB.apiBase()) { notify("Test sends need the live service (app.fenceworks.ai)."); return; }
    setSt("sending");
    try {
      if (DB.flushNow) await DB.flushNow();
      const r = await DB.automationTest({ def: a.def, channel: cfg.channel || "sms", template: cfg.template != null ? cfg.template : defaultTemplate(a) });
      setSt({ ok: true, msg: r.sentTo || "sent", warn: (r.warnings || []).join(" · ") });
    } catch (e: any) { setSt({ err: String((e && e.message) || e) }); }
  }
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
      <div className="row gap-8" style={{ flexWrap: "wrap" }}>
        <button className="btn btn-outline btn-sm" disabled={st === "sending"} onClick={test}><Icon name="send" size={13} />{st === "sending" ? "Sending…" : "Send myself a test"}</button>
        {st && st.ok && <span className="chip green" style={{ fontSize: 11 }}>Sent — {st.msg}</span>}
        {st && st.warn ? <span className="muted" style={{ fontSize: 11.5, color: "var(--amber)" }}>{st.warn}</span> : null}
        {st && st.err && <span className="muted" style={{ fontSize: 11.5, color: "var(--amber)" }}>{st.err}</span>}
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>Sends this template with sample data to YOUR phone/email (Company Settings), skipping quiet hours &amp; delay — works even while the automation is off. SMS needs A2P approval; test the Email channel until then.</p>
    </div>
  );
}

/* platform rows for the social automations — toggle per destination, with the
   live connect state so "why didn't it post" is never a mystery */
function SocialPlatformToggles({ store, cfg, setCfg, go }: any) {
  const integ = (store && store.settings && store.settings.integrations) || {};
  const plats = cfg.platforms || {};
  const [busy, setBusy] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");

  /* Start the provider's OAuth right here. Previously the only way to link a
     page was to leave this screen, hunt for the right card in Integrations,
     and come back — so a "Not connected" chip was a dead end.

     This is a full-page navigation, not a popup, on purpose: it reuses the
     existing consent round-trip exactly as Integrations does. The provider
     redirects back to the app with ?connected=<id>&ok=1, which the app already
     knows how to mirror into settings. A popup would land that callback in a
     window this page can't read. */
  async function connect(id: string) {
    const DB: any = (window as any).VFP_DB;
    if (!DB || !DB.oauthStart || !DB.apiBase || !DB.apiBase()) { go && go("integrations"); return; }
    setBusy(id); setErr("");
    try {
      const url = await DB.oauthStart(id);
      if (url === false) { setBusy(null); return; }   // chose "Not now" at the Google heads-up
      if (!url) throw new Error("Couldn't start the connection.");
      refreshReadiness();                 // re-check prerequisites when we return
      if (DB.flushNow) await DB.flushNow();   // don't lose unsaved config on navigate
      window.location.href = url;
    } catch (e: any) { setErr(String((e && e.message) || e)); setBusy(null); }
  }

  return (
    <div className="ai-field" style={{ marginBottom: 4 }}>
      <label className="field-label">Post to</label>
      {[["gbp", "Google Business Profile"], ["facebook", "Facebook Page"]].map(([id, label]) => {
        const connected = !!(integ[id] && integ[id].connected);
        const on = plats[id] !== false;
        return (
          <label key={id} className="toggle-row" onClick={() => setCfg({ platforms: { ...plats, [id]: !on } })}>
            <span className={"sw" + (on && connected ? " on" : "")}><span className="knob" /></span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
            {connected
              ? <span className="chip green" style={{ fontSize: 10.5, marginLeft: "auto" }}>Connected</span>
              : <button className="btn btn-outline btn-sm" style={{ marginLeft: "auto", padding: "3px 10px", fontSize: 11.5 }}
                  disabled={busy === id}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); connect(id); }}>
                  <Icon name="bolt" size={11} />{busy === id ? "Connecting…" : "Connect"}
                </button>}
          </label>
        );
      })}
      {err && <p className="muted" style={{ fontSize: 11.5, marginTop: 6, color: "var(--amber)" }}>{err}</p>}
    </div>
  );
}

/* Drafts waiting for approval — the queue social_post / review_share park
   posts in when "Hold for my approval" is on. One-tap Post now / Dismiss. */
function PendingSocialPosts() {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [drafts, setDrafts] = React.useState<any[]>([]);
  const [st, setSt] = React.useState<any>(live ? "loading" : "offline");
  const [busy, setBusy] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const load = React.useCallback(() => {
    if (!live || !DB.socialPending) { setSt("offline"); return; }
    setSt("loading"); setErr("");
    DB.socialPending().then((r: any) => { setDrafts(r.drafts || []); setSt("ok"); })
      .catch((e: any) => { setErr(String((e && e.message) || e)); setSt("error"); });
  }, []);
  React.useEffect(() => { load(); }, [load]);
  async function act(id: any, kind: string) {
    setBusy(id); setErr("");
    try {
      if (kind === "publish") await DB.socialPublish(id); else await DB.socialDismiss(id);
      setDrafts(ds => ds.filter((d: any) => d.id !== id));
    } catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setBusy(null); }
  }
  return (
    <div className="card pad">
      <div className="row between" style={{ marginBottom: 10 }}>
        <div className="kicker">Pending posts</div>
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load} title="Re-check"><Icon name="bolt" size={12} />Refresh</button>
      </div>
      {st === "offline" && <p className="muted" style={{ fontSize: 12, margin: 0 }}>Drafts queue on the live service (app.fenceworks.ai) — posts waiting for approval will show here.</p>}
      {st === "loading" && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Checking for drafts…</p>}
      {st === "ok" && drafts.length === 0 && <p className="muted" style={{ fontSize: 12, margin: 0 }}>Nothing waiting. When a post is drafted it lands here for one-tap approval.</p>}
      {err && <p className="muted" style={{ fontSize: 12, margin: "0 0 8px", color: "var(--amber)" }}>{st === "error" ? "Couldn't load drafts — " : ""}{err}</p>}
      {drafts.map((d: any) => {
        const post = d.post || {};
        const photos = post.photos || [];
        return (
          <div key={d.id} style={{ borderTop: "1px solid var(--line)", paddingTop: 10, marginTop: 10 }}>
            <p style={{ fontSize: 12.5, whiteSpace: "pre-wrap", margin: "0 0 8px", lineHeight: 1.5 }}>{post.message}</p>
            {photos.length > 0 && (
              <div className="row gap-6" style={{ flexWrap: "wrap", marginBottom: 8 }}>
                {photos.slice(0, 4).map((p: any, i: number) => (
                  <span key={i} style={{ width: 44, height: 44, borderRadius: 8, backgroundImage: `url(${p.url})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--line-2)", position: "relative", display: "inline-block" }}>
                    {p.tag && <span style={{ position: "absolute", bottom: 2, left: 2, fontSize: 8, fontFamily: "var(--font-mono)", textTransform: "uppercase", background: "rgba(0,0,0,.7)", color: "#fff", borderRadius: 4, padding: "1px 3px" }}>{p.tag}</span>}
                  </span>
                ))}
                {photos.length > 4 && <span className="faint" style={{ fontSize: 11, alignSelf: "center" }}>+{photos.length - 4}</span>}
              </div>
            )}
            <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn btn-primary btn-sm" disabled={busy === d.id} onClick={() => act(d.id, "publish")}><Icon name="send" size={12} />{busy === d.id ? "Posting…" : "Post now"}</button>
              <button className="btn btn-ghost btn-sm" disabled={busy === d.id} onClick={() => act(d.id, "dismiss")}>Dismiss</button>
              <span className="faint" style={{ fontSize: 10.5, marginLeft: "auto" }}>{(post.platforms || []).join(" · ")}{d.at ? " · " + new Date(d.at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* FW-023 — the words that go out when the contractor hasn't written their own.
   Mirrors WEATHER_TEMPLATE in server/src/automations.js; the server applies the
   same fallback, so an empty box can never send a blank text again. */
const WEATHER_TEMPLATE = DEFAULT_TEMPLATES.weather.fence;   // lib/servicewords.ts holds both services' words

/* Is this automation's approval gate on? Weather defaults ON (its own
   description promises the gate); everything else defaults OFF. Mirrors
   approvalRequired() in server/src/automations.js. */
const APPROVAL_BY_DEFAULT = new Set(["weather", "upsell"]);   // upsell: a cross-sell offer is read before it goes
function approvalOn(a: any, cfg: any) {
  if (cfg && cfg.requireApproval != null) return !!cfg.requireApproval;
  return APPROVAL_BY_DEFAULT.has(a.def);
}

/* FW-023 — messages held by "Require my approval before each send". That
   toggle was on every message automation's panel and read by nothing on the
   server: a contractor could switch it on, watch texts keep going out, and
   have no way to tell the setting from the behaviour. Approve sends it (or
   queues it, if quiet hours are in force — a human tapping Send doesn't make a
   9pm text legal) and the body is editable before it goes. */
function PendingMessages({ automation }: any) {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [drafts, setDrafts] = React.useState<any[]>([]);
  const [st, setSt] = React.useState<any>(live ? "loading" : "offline");
  const [busy, setBusy] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [note, setNote] = React.useState<any>("");
  const [edits, setEdits] = React.useState<any>({});
  const load = React.useCallback(() => {
    if (!live || !DB.messagesPending) { setSt("offline"); return; }
    setSt("loading"); setErr("");
    DB.messagesPending().then((r: any) => {
      const all = r.drafts || [];
      setDrafts(automation ? all.filter((d: any) => d.automation === automation) : all);
      setSt("ok");
    }).catch((e: any) => { setErr(String((e && e.message) || e)); setSt("error"); });
  }, [automation]);
  React.useEffect(() => { load(); }, [load]);
  async function act(d: any, kind: string) {
    setBusy(d.id); setErr(""); setNote("");
    try {
      if (kind === "send") {
        const r = await DB.messageApprove(d.id, edits[d.id] != null ? edits[d.id] : (d.draft || {}).body);
        if (r && r.deferred) setNote("Approved — it goes out when quiet hours lift (" + new Date(r.deferred).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" }) + ").");
        else if (r && r.skipped) setNote("Couldn't deliver it: " + r.skipped);
      } else await DB.messageDismiss(d.id);
      setDrafts(ds => ds.filter((x: any) => x.id !== d.id));
    } catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setBusy(null); }
  }
  return (
    <div className="card pad">
      <div className="row between" style={{ marginBottom: 10 }}>
        <div className="kicker">Waiting for your approval</div>
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load} title="Re-check"><Icon name="bolt" size={12} />Refresh</button>
      </div>
      {st === "offline" && <p className="muted" style={{ fontSize: 12, margin: 0 }}>Held messages queue on the live service — anything waiting for your OK shows here.</p>}
      {st === "loading" && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Checking…</p>}
      {st === "ok" && drafts.length === 0 && <p className="muted" style={{ fontSize: 12, margin: 0 }}>Nothing waiting. With approval on, every message this automation writes lands here first — nothing reaches a customer until you tap Send.</p>}
      {note && <p className="muted" style={{ fontSize: 12, margin: "0 0 8px", color: "var(--teal)" }}>{note}</p>}
      {err && <p className="muted" style={{ fontSize: 12, margin: "0 0 8px", color: "var(--amber)" }}>{st === "error" ? "Couldn't load — " : ""}{err}</p>}
      {drafts.map((d: any) => {
        const dr = d.draft || {};
        const body = edits[d.id] != null ? edits[d.id] : (dr.body || "");
        return (
          <div key={d.id} style={{ borderTop: "1px solid var(--line)", paddingTop: 10, marginTop: 10 }}>
            <textarea className="textarea" rows={3} value={body} style={{ fontSize: 12.5 }}
              onChange={e => setEdits((m: any) => ({ ...m, [d.id]: e.target.value }))} />
            <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy === d.id || !body.trim()} onClick={() => act(d, "send")}><Icon name="send" size={12} />{busy === d.id ? "Sending…" : "Send it"}</button>
              <button className="btn btn-ghost btn-sm" disabled={busy === d.id} onClick={() => act(d, "dismiss")}>Don't send</button>
              <span className="faint" style={{ fontSize: 10.5, marginLeft: "auto" }}>{dr.channel || "sms"}{d.at ? " · " + new Date(d.at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* FW-022 — how long after a job the check-in goes out. The server honoured a
   hardcoded 12 months and there was nowhere to say otherwise, so "1-year
   check-in" was the only thing this automation could ever be. Free-entry in
   months, with the common intervals one tap away: 6 for a re-seal, 18 for
   cedar, 36 for vinyl. */
const CHECKIN_PRESETS: [number, string][] = [[6, "6 mo"], [12, "1 yr"], [18, "18 mo"], [24, "2 yr"], [36, "3 yr"]];
function CheckInAfter({ cfg, setCfg, lens }: any) {
  const n = cfg.checkInAfter != null ? Number(cfg.checkInAfter) : 12;
  const valid = Number.isFinite(n) && n >= 1 && n <= 120;
  const years = valid && n >= 12 ? (n % 12 === 0 ? `${n / 12} year${n === 12 ? "" : "s"}` : `${(n / 12).toFixed(1)} years`) : null;
  return (
    <div className="ai-field" style={{ marginTop: 14 }}>
      <label className="field-label">Check in this long after the job is finished</label>
      <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <input
          className="input" type="number" min={1} max={120} step={1} style={{ width: 96 }}
          value={cfg.checkInAfter != null ? cfg.checkInAfter : 12}
          onChange={e => setCfg({ checkInAfter: e.target.value === "" ? "" : Math.max(1, Math.min(120, Math.round(+e.target.value) || 1)) })} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>months{years ? <span className="faint" style={{ fontWeight: 400 }}> · {years}</span> : null}</span>
        <div className="row gap-8" style={{ marginLeft: "auto", flexWrap: "wrap" }}>
          {CHECKIN_PRESETS.map(([m, label]) => (
            <button key={m} className={"filter-chip" + (n === m ? " on" : "")} onClick={() => setCfg({ checkInAfter: m })}>{label}</button>
          ))}
        </div>
      </div>
      <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>
        {valid
          ? <>{lens === "concrete" ? "Concrete jobs" : "Jobs"} marked <b>Completed</b> {n} month{n === 1 ? "" : "s"} ago get the message below — once each, ever. Change this and it applies from the next job that comes due.{lens === "concrete" ? " Sealer and caulk wear with every winter — a year is the usual re-seal check-in." : ""}</>
          : <span style={{ color: "var(--amber)" }}>Enter a whole number of months between 1 and 120.</span>}
      </p>
    </div>
  );
}

/* The quote follow-up's "before it expires" reminder. Quotes carry a
   valid-until date (lib/quotedetails.ts); this sends one heads-up a few days
   before it, through the same channel, quiet hours, approval setting and
   consent rules as the follow-ups above, and stops the same way (signed, paid,
   replied). The server reads a missing setting as OFF — an account from before
   this option isn't surprised by it — and a new account's first save writes it
   on (the catalog card's newCfg; lib/supabase.ts). */
const EXPIRY_REMINDER_TEMPLATE = DEFAULT_TEMPLATES.quote_expiry.fence;   // mirrors server/src/automations.js (lib/servicewords.ts)
const REMIND_DAYS: [number, string][] = [[1, "1 day"], [2, "2 days"], [3, "3 days"], [5, "5 days"], [7, "1 week"]];
function ExpiryReminder({ cfg, setCfg, lens, vcfg, setV }: any) {
  const set = cfg.expiryReminder && typeof cfg.expiryReminder === "object" ? cfg.expiryReminder : null;
  // under the Concrete lens the words are the concrete ones (byVertical.concrete.expiryTemplate); on/off and the days are shared
  const cx = lens === "concrete";
  const DB: any = (window as any).VFP_DB;
  const fresh = !!(DB && DB.aiFresh && DB.aiFresh());   // a new workspace: the on-by-default applies until saved
  const er = set || { on: fresh, days: 3 };
  const days = [1, 2, 3, 5, 7].includes(Number(er.days)) ? Number(er.days) : 3;
  const patch = (p: any) => setCfg({ expiryReminder: { on: !!er.on, days, ...(typeof er.template === "string" ? { template: er.template } : {}), ...p } });
  return (
    <div className="ai-field" style={{ marginTop: 14 }} data-expiry-reminder>
      <MiniToggle on={!!er.on} onClick={() => patch({ on: !er.on })} label="Remind them before the quote expires" />
      {er.on && (
        <>
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap", margin: "8px 0 0 44px" }}>
            {REMIND_DAYS.map(([d, label]) => (
              <button key={d} className={"filter-chip" + (days === d ? " on" : "")} onClick={() => patch({ days: d })}>{label}</button>
            ))}
            <span className="muted" style={{ fontSize: 12.5 }}>before the valid-until date</span>
          </div>
          <textarea className="textarea" rows={3} style={{ marginTop: 8 }}
            value={cx ? (typeof vcfg.expiryTemplate === "string" ? vcfg.expiryTemplate : DEFAULT_TEMPLATES.quote_expiry.concrete) : (typeof er.template === "string" ? er.template : EXPIRY_REMINDER_TEMPLATE)}
            onChange={e => (cx ? setV({ expiryTemplate: e.target.value }) : patch({ template: e.target.value }))} />
          <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Variables: <code>{"{customer}"}</code> <code>{"{company}"}</code> <code>{"{expires}"}</code> (the valid-until date) <code>{"{link}"}</code> (their quote). One message per quote, only for quotes with a valid-until date — same channel, quiet hours and approval as the follow-ups, and none once they've signed, paid or replied.</p>
        </>
      )}
    </div>
  );
}

function ManageControls({ a, cfg, setCfg, store, go, ready }: any) {
  const ct = a.channelType;
  const sms = ready && ready.sms;
  const need = ready && ready.automations && ready.automations[a.needs || a.id];
  // Under the Concrete lens the WORDS and thresholds are the concrete ones:
  // config.byVertical.concrete (template, templates, threshold, minTemp,
  // dewSpread, checkInAfter, expiryTemplate). Channel, quiet hours, delay and
  // approval are the workspace's and stay shared. The server resolves the same
  // way (server/src/servicewords.js resolveTemplate / configFor).
  const lens = store && store.lens === "concrete" ? "concrete" : "fence";
  const cx = lens === "concrete";
  const vcfg = cx ? ((cfg.byVertical && cfg.byVertical.concrete) || {}) : cfg;
  const setV = cx ? (p: any) => setCfg({ byVertical: { ...(cfg.byVertical || {}), concrete: { ...vcfg, ...p } } }) : setCfg;
  const runsConcrete = enabledVerticals(store && store.settings, store && store.enabledLenses).includes("concrete");
  const LensNote = () => (cx ? <p className="faint" style={{ fontSize: 11, margin: "0 0 10px" }}>Concrete words and thresholds — the fence ones are edited under the Fence lens. Channel, quiet hours, delay and approval are shared.</p> : null);

  if (ct === "message") {
    const qhRaw = cfg.quietHours || "8am – 8pm only";
    const qhCustom = String(qhRaw).startsWith("custom:");
    const qhm = qhCustom ? (String(qhRaw).match(/^custom:(\d{1,2})-(\d{1,2})$/) || [0, 8, 21]) : null;
    return (
      <>
        <Prereq need={need} go={go} />
        <SendChannel value={cfg.channel} onChange={v => setCfg({ channel: v })} sms={sms} go={go} />
        <div className="ai-drawer-grid">
          <FieldSelect label="Trigger delay" value={cfg.delay || "immediate"} onChange={v => setCfg({ delay: v })} options={DELAY_OPTS} />
          <FieldSelect label="Quiet hours" value={qhCustom ? "custom" : qhRaw} onChange={v => setCfg({ quietHours: v === "custom" ? "custom:8-21" : v })} options={QUIET_OPTS} />
        </div>
        {qhCustom && (
          <>
            <div className="ai-drawer-grid" style={{ marginTop: 10 }}>
              <FieldSelect label="No sends before" value={String(+qhm[1])} onChange={v => setCfg({ quietHours: `custom:${v}-${+qhm[2]}` })} options={QH_HOURS} />
              <FieldSelect label="No sends after" value={String(+qhm[2])} onChange={v => setCfg({ quietHours: `custom:${+qhm[1]}-${v}` })} options={QH_HOURS} />
            </div>
            <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Your business's local time. Federal TCPA allows 8am–9pm; several states are stricter — set what your state requires.</p>
          </>
        )}
        {a.def === "warranty" && <CheckInAfter cfg={vcfg} setCfg={setV} lens={lens} />}
        <div className="ai-field" style={{ marginTop: 14 }}>
          <label className="field-label">Message template{cx ? " · concrete" : ""}</label>
          <LensNote />
          <textarea className="textarea" rows={3} value={vcfg.template != null ? vcfg.template : defaultTemplate(a, lens)} onChange={e => setV({ template: e.target.value })} />
          <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Variables: <code>{"{customer}"}</code> <code>{"{company}"}</code> <code>{"{link}"}</code> <code>{"{date}"}</code> <code>{"{time}"}</code> — date &amp; time fill from the booked appointment when there is one{cx ? "; {company} and {link} are the concrete brand's" : ""}</p>
        </div>
        {a.def === "quote_followup" && <ExpiryReminder cfg={cfg} setCfg={setCfg} lens={lens} vcfg={vcfg} setV={setV} />}
        <MiniToggle on={cfg.requireApproval} onClick={() => setCfg({ requireApproval: !cfg.requireApproval })} label="Require my approval before each send" />
        <TestSendRow a={a} cfg={{ ...cfg, template: vcfg.template != null ? vcfg.template : defaultTemplate(a, lens) }} />
      </>
    );
  }

  /* Customer job updates — four moments, each independently switchable, each
     with its own message. One automation instead of the old On-my-way / Job-
     status pair, so a single event can't fire two texts. */
  if (ct === "jobevents") {
    const events = cfg.events || { enroute: true };
    // the words per moment, per service (lib/servicewords.ts); the concrete ones under the Concrete lens
    const tpls = (cx ? vcfg.templates : cfg.templates) || {};
    const setTpls = (next: any) => setV({ templates: next });
    const EVENTS: any[] = [
      ["enroute", "Crew is on the way", "Sent when the crew taps On my way on the job.", JOB_STATUS_TEMPLATES.enroute[lens]],
      ["started", "Work has started", "Sent when the job is marked In progress.", JOB_STATUS_TEMPLATES.started[lens]],
      ["completed", "Job is finished", "Sent when the work order is marked Complete.", JOB_STATUS_TEMPLATES.completed[lens]],
      ["delayed", cx ? "Appointment pushed" : "Install pushed", cx ? "Sent when you reschedule a booked job." : "Sent when you reschedule a booked install.", JOB_STATUS_TEMPLATES.delayed[lens]],
    ];
    return (
      <>
        <Prereq need={need} go={go} />
        <SendChannel value={cfg.channel} onChange={v => setCfg({ channel: v })} sms={sms} go={go} />
        <div className="ai-drawer-grid">
          <FieldSelect label="Quiet hours" value={cfg.quietHours || "8am – 8pm only"} onChange={v => setCfg({ quietHours: v })} options={QUIET_OPTS.filter(o => o[0] !== "custom")} />
        </div>
        <div className="ai-field" style={{ marginTop: 14 }}>
          <label className="field-label">Send an update when…</label>
          <LensNote />
          {EVENTS.map(([key, label, note, fallback]) => {
            const on = !!events[key];
            return (
              <div key={key} style={{ borderTop: "1px solid var(--line)", paddingTop: 10, marginTop: 10 }}>
                <label className="toggle-row" style={{ marginTop: 0 }} onClick={() => setCfg({ events: { ...events, [key]: !on } })}>
                  <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                </label>
                <p className="faint" style={{ fontSize: 11, margin: "3px 0 0 44px" }}>{note}</p>
                {on && (
                  <textarea className="textarea" rows={2} style={{ marginTop: 8 }}
                    value={tpls[key] != null ? tpls[key] : fallback}
                    onChange={e => setTpls({ ...tpls, [key]: e.target.value })} />
                )}
              </div>
            );
          })}
          {!Object.values(events).some(Boolean) && <p className="faint" style={{ fontSize: 11.5, marginTop: 8, color: "var(--amber)" }}>Nothing selected — no updates will go out.</p>}
        </div>
        <TestSendRow a={a} cfg={{ ...cfg, template: tpls.enroute || EVENTS[0][3] }} />
      </>
    );
  }

  if (ct === "voice") {
    if (a.def === "web_chat") {
      return (
        <>
          <div className="pe-callout info" style={{ marginBottom: 14 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Text widget — replies happen in chat, so there's no SMS/Email send channel. <b style={{ color: "var(--fg-2)" }}>Install it:</b> Settings → Estimator Widget → "AI Chat Bubble" — one copy-paste snippet for your website.</p></div>
          <FieldSelect label="Chat tone" value={cfg.tone || "friendly"} onChange={v => setCfg({ tone: v })} options={[["friendly", "Friendly"], ["professional", "Professional"], ["casual", "Casual"]]} />
          <div className="ai-field" style={{ marginTop: 14 }}><label className="field-label">Opening message</label>
            <textarea className="textarea" rows={2} value={cfg.template != null ? cfg.template : "Hi! 👋 Want a quick fence estimate? Tell me your zip and fence type."} onChange={e => setCfg({ template: e.target.value })} /></div>
          <MiniToggle on={cfg.captureFirst} onClick={() => setCfg({ captureFirst: !cfg.captureFirst })} label="Ask for name & number before detailed pricing" />
        </>
      );
    }
    // bilingual
    return (
      <>
        <div className="pe-callout info" style={{ marginBottom: 14 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Runs on the voice agent — when Spanish is detected it switches voice &amp; prompt language (es). No SMS/Email channel.</p></div>
        <FieldSelect label="Spanish agent voice" value={cfg.spanishVoice || "11labs-andrea-es"} onChange={v => setCfg({ spanishVoice: v })} options={[["11labs-andrea-es", "Andrea — warm (es)"], ["11labs-diego-es", "Diego — professional (es)"], ["11labs-sofia-es", "Sofía — friendly (es)"]]} />
        <div className="ai-field" style={{ marginTop: 14 }}><label className="field-label">Spanish greeting</label>
          <textarea className="textarea" rows={2} value={cfg.template != null ? cfg.template : "¡Gracias por llamar a {company}! ¿En qué puedo ayudarle con su cerca?"} onChange={e => setCfg({ template: e.target.value })} /></div>
      </>
    );
  }

  if (ct === "rules") {
    // the rules a workspace starts with: the pair the crew checklist can tick
    // when it runs concrete, the old fence-only trio otherwise (lib/servicewords.ts)
    const rules = cfg.rules || (runsConcrete ? CROSS_SELL_RULES : LEGACY_UPSELL_RULES);
    const approve = cfg.requireApproval !== false;
    return (
      <>
        <p className="faint" style={{ fontSize: 11.5, margin: "0 0 10px", lineHeight: 1.5 }}><b style={{ color: "var(--fg-2)" }}>When</b> is what the crew ticked on the on-site checklist in Crew Mode ("Noticed sunken or cracked concrete", "Noticed an old or leaning fence"); <b style={{ color: "var(--fg-2)" }}>then</b> names the work to offer. The offer goes out from the service that does it — a fence crew's concrete note is sent as {concreteBrandName(store) || "the company"}, with the concrete estimate form as the link.</p>
        <RuleRows rules={rules} onChange={r => setCfg({ rules: r })} />
        <SendChannel value={cfg.channel} onChange={v => setCfg({ channel: v })} sms={sms} go={go} />
        <div className="ai-drawer-grid">
          <FieldSelect label="Send the offer" value={cfg.delay || "immediate"} onChange={v => setCfg({ delay: v })} options={DELAY_OPTS.map(([k, l]) => [k, k === "immediate" ? "When the job is marked complete" : l])} />
          <FieldSelect label="Quiet hours" value={cfg.quietHours || "8am – 8pm only"} onChange={v => setCfg({ quietHours: v })} options={QUIET_OPTS.filter(o => o[0] !== "custom")} />
        </div>
        <div className="ai-field" style={{ marginTop: 14 }}>
          <label className="field-label">The offer</label>
          <textarea className="textarea" rows={3} value={vcfg.template != null ? vcfg.template : templateFor("upsell", lens)} onChange={e => setV({ template: e.target.value })} />
          <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Variables: <code>{"{customer}"}</code> <code>{"{noticed}"}</code> (what the crew ticked) <code>{"{offer}"}</code> (the rule's "then") <code>{"{company}"}</code> and <code>{"{link}"}</code> (the service that does the work)</p>
        </div>
        <MiniToggle on={approve} onClick={() => setCfg({ requireApproval: !approve })} label="Hold each offer for my approval" note="recommended" />
        <MiniToggle on={cfg.surfaceOnCall !== false} onClick={() => setCfg({ surfaceOnCall: !(cfg.surfaceOnCall !== false) })} label="Log matched suggestions on the job when it books" />
        <MiniToggle on={cfg.message !== false} onClick={() => setCfg({ message: !(cfg.message !== false) })} label="Send the customer the offer when the job is done" />
      </>
    );
  }

  if (ct === "schedule") {
    if (a.def === "weather") {
      // FW-023: this automation drafts a text to the CUSTOMER, and had neither
      // a template field (so it rendered blank) nor the approval gate its own
      // description promises. Both live here now.
      const approve = cfg.requireApproval !== false;
      const D = WEATHER_DEFAULTS.concrete.bond;
      return (
        <>
          <LensNote />
          {cx ? (
            <>
              <p className="faint" style={{ fontSize: 11.5, margin: "0 0 10px", lineHeight: 1.5 }}>A coating, sealer or caulk day needs a dry slab, air and slab above the minimum, and the dew point well below the day's high. A lift is forgiving — a downpour or frozen ground stops it. A cleaning day only needs the water not to freeze.</p>
              <Slider label="Flag a coating / sealing day when rain chance ≥" value={vcfg.threshold != null ? vcfg.threshold : D.rain} min={10} max={100} step={5} suffix="%" onChange={v => setV({ threshold: v })} />
              <Slider label="…or the overnight low is at or below" value={vcfg.minTemp != null ? vcfg.minTemp : D.minTemp} min={20} max={60} step={1} suffix="°F" onChange={v => setV({ minTemp: v })} />
              <Slider label="…or the dew point is within this of the high" value={vcfg.dewSpread != null ? vcfg.dewSpread : D.dewSpread} min={2} max={15} step={1} suffix="°F" onChange={v => setV({ dewSpread: v })} />
            </>
          ) : (
            <Slider label="Trigger when rain chance ≥" value={cfg.threshold != null ? cfg.threshold : 60} min={20} max={100} step={5} suffix="%" onChange={v => setCfg({ threshold: v })} />
          )}
          <MiniToggle on={cfg.notifyCrew !== false} onClick={() => setCfg({ notifyCrew: !(cfg.notifyCrew !== false) })} label={cx ? "Flag the crew when a job day is at risk" : "Flag the crew when an install day is at risk"} />
          <SendChannel value={cfg.channel} onChange={v => setCfg({ channel: v })} sms={sms} go={go} />
          <div className="ai-field" style={{ marginTop: 14 }}>
            <label className="field-label">Reschedule note to the customer{cx ? " · concrete" : ""}</label>
            <textarea className="textarea" rows={3} value={vcfg.template != null ? vcfg.template : DEFAULT_TEMPLATES.weather[lens]} onChange={e => setV({ template: e.target.value })} />
            <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Variables: <code>{"{customer}"}</code> <code>{"{company}"}</code> <code>{"{date}"}</code> <code>{"{link}"}</code>{cx ? <> <code>{"{work}"}</code> (sealing, the floor coating, lifting…)</> : null}</p>
          </div>
          <MiniToggle on={approve} onClick={() => setCfg({ requireApproval: !approve })} label="Hold each note for my approval" note="recommended" />
          {!approve && <p className="faint" style={{ fontSize: 11.5, marginTop: 6, color: "var(--amber)" }}>With this off, the note texts the customer automatically the morning the forecast trips your threshold. It still never moves the job on your schedule.</p>}
        </>
      );
    }
    // diggers — the locate is legally required before every dig, so the
    // reminder is multi-select and defaults to ALL of them. Missing it stops
    // the job (or hits a gas line), which is not a one-channel risk.
    {
      const via = Array.isArray(cfg.remindVia) ? cfg.remindVia : cfg.remindVia ? [cfg.remindVia] : ["sms", "email", "calendar"];
      const toggleVia = (v) => setCfg({ remindVia: via.includes(v) ? via.filter((x) => x !== v) : [...via, v] });
      const smsLocked = sms && !sms.ready;
      return (
        <>
          <Slider label="Schedule the locate" value={cfg.daysBefore != null ? cfg.daysBefore : 3} min={1} max={10} step={1} suffix=" days before dig" onChange={v => setCfg({ daysBefore: v })} />
          <div className="ai-field">
            <label className="field-label">Remind me via <span className="faint" style={{ fontWeight: 400 }}>— pick any</span></label>
            {[["sms", "Text my phone", smsLocked], ["email", "Email me", false], ["calendar", "Put a hold on the schedule", false]].map(([id, label, locked]: any) => (
              <label key={id} className="toggle-row" onClick={() => { if (!locked) toggleVia(id); }} style={locked ? { opacity: .5, cursor: "not-allowed" } : undefined}>
                <span className={"sw" + (via.includes(id) && !locked ? " on" : "")}><span className="knob" /></span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                {locked ? <span className="chip" style={{ fontSize: 10.5, marginLeft: "auto" }}>SMS locked</span> : null}
              </label>
            ))}
            {!via.length && <p className="faint" style={{ fontSize: 11.5, marginTop: 6, color: "var(--amber)" }}>Nothing selected — you won't be reminded at all.</p>}
          </div>
          <div className="ai-field" style={{ marginTop: 14 }}>
            <label className="field-label">Reminder text</label>
            <textarea className="textarea" rows={2} value={cfg.template != null ? cfg.template : defaultTemplate(a)} onChange={e => setCfg({ template: e.target.value })} />
            <p className="faint" style={{ fontSize: 11, marginTop: 5 }}>Goes to <b>you</b>, not the customer. Variables: <code>{"{customer}"}</code> <code>{"{date}"}</code></p>
          </div>
          {/* FW-028: this was "Auto-file the 811 request (where supported)" and
              nothing was ever filed — the whole implementation was writing an
              events row saying it had. It can't be made true: 811 is ~60
              separate state one-call centers with no national API, the ones
              with an API gate it behind excavator membership, and several
              require the ticket to be filed by whoever is doing the digging.
              A ticket a SaaS vendor files is not a valid ticket in most
              states. So the software does the part it legitimately can. */}
          <div className="pe-callout info" style={{ marginTop: 14 }}>
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              <b>Filing the ticket is yours.</b> Every state runs its own 811 center and most require the excavator to file and attest to the site, so nobody can file it for you. What this does instead: when a job books, it works out which center covers the address, when the request has to be in by, and fills in every field the form asks for — open the portal from the work order and paste. About thirty seconds instead of ten minutes.
            </p>
          </div>
        </>
      );
    }
  }

  // internal
  if (a.def === "social_post") {
    return (
      <>
        <div className="pe-callout info" style={{ marginBottom: 14 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Fires when a work order is marked <b>Completed</b>. Tag the job's photos <b>Before / After</b> on the work order and they lead the post (street address never appears — town only). Connect your pages in Integrations.</p></div>
        <SocialPlatformToggles store={store} cfg={cfg} setCfg={setCfg} go={go} />
        <FieldSelect label="Caption tone" value={cfg.tone || "warm"} onChange={v => setCfg({ tone: v })} options={[["warm", "Warm"], ["professional", "Professional"], ["high-energy", "High-energy"]]} />
        <MiniToggle on={cfg.requireApproval !== false} onClick={() => setCfg({ requireApproval: !(cfg.requireApproval !== false) })} label="Hold each post for my approval" note="recommended" />
        <MiniToggle on={cfg.requirePhotos !== false} onClick={() => setCfg({ requirePhotos: !(cfg.requirePhotos !== false) })} label="Skip jobs that have no photos" />
      </>
    );
  }
  if (a.def === "review_share") {
    return (
      <>
        <div className="pe-callout info" style={{ marginBottom: 14 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Connect <b>Google Business Profile</b> in Integrations to detect new reviews (polled every ~30 min). Reviews at or above your threshold become a thank-you post on the pages below.</p></div>
        <SocialPlatformToggles store={store} cfg={cfg} setCfg={setCfg} go={go} />
        <FieldSelect label="Share reviews rated" value={String(cfg.minStars != null ? cfg.minStars : 4)} onChange={v => setCfg({ minStars: +v })} options={[["4", "4★ and up"], ["5", "5★ only"]]} />
        <MiniToggle on={cfg.requireApproval !== false} onClick={() => setCfg({ requireApproval: !(cfg.requireApproval !== false) })} label="Hold each post for my approval" note="recommended" />
      </>
    );
  }
  if (a.def === "lead_parser") {
    return (
      <>
        <TagList label="Lead sources to parse" tags={cfg.sources || ["Angi", "Nextdoor", "Facebook", "Web form"]} placeholder="Add a source…" onChange={s => setCfg({ sources: s })} />
        <MiniToggle on={cfg.autoSpeedToLead !== false} onClick={() => setCfg({ autoSpeedToLead: !(cfg.autoSpeedToLead !== false) })} label="Chain into Speed-to-lead after parsing" />
      </>
    );
  }
  if (a.def === "review_responses") {
    return (
      <>
        <div className="pe-callout info" style={{ marginBottom: 14 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>Connect <b>Google Business Profile</b> in Integrations to pull reviews. New reviews are polled every ~30 min and drafted here for approval.</p></div>
        <FieldSelect label="Reply tone" value={cfg.tone || "warm"} onChange={v => setCfg({ tone: v })} options={[["warm", "Warm"], ["professional", "Professional"], ["brief", "Brief"]]} />
        <MiniToggle on={cfg.flagLow !== false} onClick={() => setCfg({ flagLow: !(cfg.flagLow !== false) })} label="Flag 3★ and under for me instead of drafting" />
      </>
    );
  }
  if (a.def === "permit") {
    return (
      <>
        <TagList label="Jurisdictions that require a permit" tags={cfg.jurisdictions || defaultJurisdictions(store)} placeholder="Add a county / city\u2026" onChange={j => setCfg({ jurisdictions: j })} />
        <MiniToggle on={cfg.blockSchedule} onClick={() => setCfg({ blockSchedule: !cfg.blockSchedule })} label="Block scheduling until the permit is filed" />
      </>
    );
  }
  if (a.def === "low_stock") {
    return (
      <>
        <FieldSelect label="Preferred supplier" value={cfg.supplier || "Northern Supply Co."} onChange={v => setCfg({ supplier: v })} options={[["Northern Supply Co.", "Northern Supply Co."], ["Midwest Fence Wholesale", "Midwest Fence Wholesale"], ["Local lumberyard", "Local lumberyard"]]} />
        <MiniToggle on={cfg.requireApproval !== false} onClick={() => setCfg({ requireApproval: !(cfg.requireApproval !== false) })} label="Draft the PO for my approval (don't auto-send)" />
      </>
    );
  }
  // dispatch
  return (
    <>
      <Slider label="Max dispatch radius" value={cfg.maxRadius != null ? cfg.maxRadius : 25} min={5} max={60} step={5} suffix=" mi" onChange={v => setCfg({ maxRadius: v })} />
      <MiniToggle on={cfg.autoNotify} onClick={() => setCfg({ autoNotify: !cfg.autoNotify })} label="Auto-notify the assigned crew" />
    </>
  );
}

/* ---- full automation management screen ---- */
function AutomationPage({ store, go, params }: any) {
  const ready = useReadiness();
  const ai = (store.settings && store.settings.ai) || {};
  const autos = ai.automations || {};
  const cfgs = ai.automationCfg || {};
  const a = AI_AUTOMATIONS.find(x => x.id === (params && params.autoId)) || AI_AUTOMATIONS[0];
  const on = a.soon ? false : (autos[a.id] != null ? autos[a.id] : a.default);
  const cfg = cfgs[a.id] || {};
  const setAuto = (v) => { if (a.soon) return; store.setSettings({ ai: Object.assign({}, ai, { automations: Object.assign({}, autos, { [a.id]: v }) }) }); };
  const setCfg = (p) => store.setSettings({ ai: Object.assign({}, ai, { automationCfg: Object.assign({}, cfgs, { [a.id]: Object.assign({}, cfg, p) }) }) });
  const CT_LABEL = { message: "Messaging", jobevents: "Messaging", voice: "Voice / chat", rules: "Rules", schedule: "Schedule", internal: "Internal" };
  return (
    <div className="page fade-up" style={{ maxWidth: 880 }}>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => go("ai", { tab: "automations" })}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />All automations</button>
      <div className="row between" style={{ marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div className="row gap-12">
          <span className="ai-phone-ic" style={on ? { background: "var(--teal-tint)", borderColor: "var(--teal-tint-2)", color: "var(--teal)" } : { background: "var(--surface-2)", borderColor: "var(--line-2)", color: "var(--fg-3)" }}><Icon name={a.icon} size={20} /></span>
          <div>
            <div className="row gap-8" style={{ alignItems: "center" }}><h1 className="display" style={{ fontSize: 24, margin: 0 }}>{a.name}</h1>{a.star && <span className="ai-star" style={{ fontSize: 16 }}>★</span>}</div>
            <span className={"chip " + (a.soon ? "" : (on ? "green" : ""))} style={{ marginTop: 4 }}>{a.soon ? "Coming soon" : (on ? "Active" : "Paused")} · {a.group} · {CT_LABEL[a.channelType]}</span>
          </div>
        </div>
        {a.soon
          ? <span className="chip" style={{ alignSelf: "center" }}>Coming soon</span>
          : <label className="toggle-row" style={{ width: "auto", alignSelf: "center" }} onClick={() => setAuto(!on)}><span className={"sw" + (on ? " on" : "")}><span className="knob" /></span><span style={{ fontSize: 13, fontWeight: 700 }}>{on ? "On" : "Off"}</span></label>}
      </div>
      {a.soon && (
        <div className="card pad" style={{ borderColor: "var(--amber-tint, var(--line))", marginBottom: 16 }}>
          <div className="row gap-8" style={{ alignItems: "flex-start" }}>
            <Icon name="bolt" size={15} className="muted" />
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>This automation isn't live yet — it needs an event source we haven't wired (e.g. an inbound-email feed, review webhook, or estimate-booking step). It's on the near-term roadmap; the settings below are a preview of how it'll work.</p>
          </div>
        </div>
      )}

      <div className="ai-page-grid">
        <div style={{ display: "grid", gap: 16 }}>
          <div className="card pad">
            <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, margin: "0 0 16px" }}>{a.blurb}</p>
            <ManageControls a={a} cfg={cfg} setCfg={setCfg} store={store} go={go} ready={ready} />
          </div>
        </div>
        <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
          {(a.def === "social_post" || a.def === "review_share") && <PendingSocialPosts />}
          {(a.channelType === "message" || a.def === "weather" || a.def === "upsell") && approvalOn(a, cfg) && <PendingMessages automation={a.def} />}
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 12 }}>Performance · 30 days</div>
            <div className="ai-drawer-stats" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
              <div><div className="display" style={{ fontSize: 20, color: "var(--teal)" }}>0</div><div className="muted" style={{ fontSize: 11 }}>fired</div></div>
              <div><div className="display" style={{ fontSize: 20 }}>—</div><div className="muted" style={{ fontSize: 11 }}>reply rate</div></div>
              <div><div className="display" style={{ fontSize: 20 }}>0</div><div className="muted" style={{ fontSize: 11 }}>jobs</div></div>
            </div>
            <p className="faint" style={{ fontSize: 11, margin: "12px 0 0", lineHeight: 1.5 }}>No activity yet — live counts appear here once this automation fires.</p>
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 10 }}>Wiring</div>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div className="row between"><span>Listens for</span><code style={{ color: "var(--fg-2)" }}>{a.trig}</code></div>
              <div className="row between"><span>Definition</span><code style={{ color: "var(--fg-2)" }}>{a.def}</code></div>
              {a.id === "lead_parser" && <div className="row between" style={{ marginTop: 4, alignItems: "flex-start", gap: 8 }}><span>Inbound URL</span><code style={{ color: "var(--fg-2)", fontSize: 10, wordBreak: "break-all", textAlign: "right", maxWidth: "62%" }}>{(((window as any).VFP_DB?.apiBase?.()) || "https://api.fenceworks.ai") + "/webhooks/inbound-email/" + (((window as any).VFP_DB?.ws?.()) || "…")}</code></div>}
            </div>
          </div>
          <div className="pe-callout info">
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>Metrics start counting once your send channels are connected in <a onClick={() => go("integrations")} style={{ color: "var(--teal)", cursor: "pointer" }}>Integrations</a> and this automation fires.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
/* Permit jurisdictions used to be hardcoded to two Wisconsin counties, so a
   contractor in Montana opened this automation and was told Brown County and
   Outagamie County need permits. Seed from the workspace's OWN service area
   instead — the counties/cities it actually quotes in — and fall back to empty
   (prompting them to add their own) rather than to someone else's state. */
function defaultJurisdictions(store: any) {
  const s = (store && store.settings) || {};
  const co = s.company || (store && store.company) || {};
  const out: string[] = [];
  const push = (v: any) => { const t = String(v || "").trim(); if (t && !out.includes(t)) out.push(t); };
  // an explicit service-area list wins
  (Array.isArray(s.serviceAreas) ? s.serviceAreas : []).forEach(push);
  (Array.isArray(s.counties) ? s.counties : []).forEach(push);
  if (!out.length) {
    // else the company's own city/county — the one jurisdiction we can be sure of
    push(s.companyCounty || co.county);
    push(s.companyCity || co.city);
  }
  return out;
}

/* the card's default words for a service (lib/servicewords.ts — the server
   resolves the same table). Cards are keyed by their definition id there; the
   older card ids still resolve for callers that pass one (messages.tsx). */
const DEF_OF: Record<string, string> = { missed_call_text: "missed_call", review_request: "auto_review", review_interceptor: "neg_review", diggers_811: "diggers", neighborhood_farm: "farming", ar_followup: "invoice", speed_to_lead: "speed_lead", financing_prequal: "financing", owner_summary: "pipeline", warranty_checkin: "warranty", attach_upsell: "upsell", reschedule_weather: "weather" };
function defaultTemplate(a: any, vertical?: any) {
  const id = a && (a.def || DEF_OF[a.id] || a.id);
  return templateFor(id, vertical);
}
/* the concrete brand's name, for the words on the cards */
function concreteBrandName(store: any) {
  const b = brandOf({ company: store && (store.company || (store.settings && store.settings.company)), settings: store && store.settings }, "concrete");
  return b && !b.isCompany ? b.name : "";
}

// expose the catalog + the message-default helper so the persistence layer
// (supabase.js) can key ai_automations by canonical id and materialize the
// same send defaults the Manage screen shows.
Object.assign(window, { AISuite, AI_AUTOMATIONS, AutomationPage });
window.VFP_AI = { defaultTemplate: defaultTemplate, catalog: AI_AUTOMATIONS };

export { chanFor, ChanChip, AISuite, SendChannel, MiniToggle, FieldSelect, Slider, TagList, RuleRows, ManageControls, AutomationPage, defaultTemplate, AI_AUTOMATIONS, CHAN, QUIET_OPTS, DELAY_OPTS };
